import { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, FileX, Plus, Printer, Download, Check, Pencil } from 'lucide-react';

import { CaseTopBar } from '../components/editor/CaseTopBar';
import { DocumentsPanel } from '../components/editor/DocumentsPanel';
import { EntityTypePickerPopover } from '../components/editor/EntityTypePickerPopover';
import { DeleteCaseDialog } from '../components/cases/DeleteCaseDialog';
import { DeleteDocumentDialog } from '../components/cases/DeleteDocumentDialog';
import { ReprocessDocumentDialog } from '../components/cases/ReprocessDocumentDialog';
import { Button } from '../components/ui/Button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getFileIconByName } from '../components/fileIconUtils';
import {
  casesApi,
  documentsApi,
  redactionApi,
  pstApi,
  dictionaryApi,
  apiHelpers,
  type HtmlContent,
  type ReprocessMode,
  type TextInRegionResponse,
  type EntitySuggestionsResponse,
  type DocumentUploadResponse,
} from '../lib/api';
import { uploadDocumentWithFallback } from '../lib/uppyUpload';
import { DuplicateUploadDialog } from '../components/cases/DuplicateUploadDialog';
import { useReasons } from '../hooks/useReasons';
import { useResultsPanelDock } from '../hooks/useResultsPanelDock';
import type { Case, CaseDocument, DocumentStatus, EntitySuggestion, EntityType, RedactionTool } from '../types';
import { DOCUMENT_STATUS_CONFIG } from '../types';
import { cn } from '../components/ui/utils';
import type { PdfSource } from '../components/pdf/PDFViewer';
import { PdfPageSkeleton } from '../components/pdf/PdfPageSkeleton';
import { getAuthTokensService } from '../lib/auth/AuthTokensService';

const ResultsPanel = lazy(() => import('../components/editor/ResultsPanel').then(m => ({ default: m.ResultsPanel })));
// Kept callable so the chunk can be preloaded on page mount (in parallel with
// the case fetch) — by the time the first document auto-selects, the editor
// shell is almost always ready to render synchronously. Vite dedupes the import.
const importPDFEditor = () => import('../components/pdf/PDFEditor');
const PDFEditor = lazy(() => importPDFEditor().then(m => ({ default: m.PDFEditor })));
const DocxEditorView = lazy(() => import('../components/docx/DocxEditorView').then(m => ({ default: m.DocxEditorView })));
const EmailEditorView = lazy(() => import('../components/email/EmailEditorView').then(m => ({ default: m.EmailEditorView })));
const PSTEditorView = lazy(() => import('../components/pst/PSTEditorView').then(m => ({ default: m.PSTEditorView })));
const CaseWizardModal = lazy(() => import('../components/cases/CaseWizardModal').then(m => ({ default: m.CaseWizardModal })));
const CaseDictionarySetupModal = lazy(() => import('../components/cases/CaseDictionarySetupModal').then(m => ({ default: m.CaseDictionarySetupModal })));

// Build the per-document progress structures from the full redaction-job list.
// Shared by the initial case load and the polling refresh so the progress bars
// render in the same paint as the document badges (instead of popping in a beat
// later once a second request resolves).
// Job statuses that carry reviewable detection results. Counts for the sidebar
// progress bar are sourced only from these, so a queued/processing/failed job
// (e.g. a freshly-started or failed reprocess) can't shadow an earlier
// successful job and make the bar read detected=0 / vanish.
const RESULT_STATUSES = new Set(['awaiting_approval', 'approved', 'completed']);
const EMPTY_SUGGESTIONS: EntitySuggestion[] = [];
const EMPTY_READY_PAGES: number[] = [];

// Trigger a browser download from either a (presigned S3) URL or an in-memory blob.
// A URL is navigated to directly — a navigation isn't subject to CORS, unlike an XHR
// following a cross-origin 307 redirect to S3 (which the browser blocks). For a blob
// we save an object URL. Exactly one of `url`/`blob` should be set.
function triggerBrowserSave(target: { url?: string; blob?: Blob; filename?: string }): void {
  const a = document.createElement('a');
  let objectUrl: string | undefined;
  if (target.url) {
    a.href = target.url;
    // S3's signed Content-Disposition carries the filename; the download attribute
    // is ignored for cross-origin URLs but harmless to set.
    a.rel = 'noopener';
  } else if (target.blob) {
    objectUrl = URL.createObjectURL(target.blob);
    a.href = objectUrl;
    if (target.filename) a.download = target.filename;
  } else {
    return;
  }
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  if (objectUrl) URL.revokeObjectURL(objectUrl);
}

// Save a document version to disk. When the file is stored as-is on S3 the backend
// returns a presigned attachment URL we navigate to directly; otherwise we fetch the
// proxied /download route (with its auth header) into a blob and save that.
async function saveDocumentToDisk(
  documentId: string,
  version: 'original' | 'redacted',
  options: { fallbackFilename?: string; format?: 'pdf' } = {}
): Promise<void> {
  const { fallbackFilename, format } = options;
  let resolved: { url: string; presigned: boolean } | null = null;
  try {
    resolved = await documentsApi.resolveDownloadUrl(documentId, version, format);
  } catch {
    // Fall through to the proxied blob download below.
  }

  if (resolved?.presigned) {
    triggerBrowserSave({ url: resolved.url });
    return;
  }

  const { blob, filename } = await documentsApi.downloadDocument(documentId, version, fallbackFilename, format);
  triggerBrowserSave({ blob, filename });
}

// Fetch a document version's bytes as a blob, for cases that need the bytes in JS
// (e.g. opening a print window from a same-origin blob URL). Prefers a direct fetch
// of the presigned S3 URL — a direct cross-origin GET works (it's what PDF.js
// preview does); only an XHR *following a redirect* to S3 is what S3 blocks via
// CORS. Falls back to the proxied /download route when presigning isn't available.
async function fetchDocumentBlob(
  documentId: string,
  version: 'original' | 'redacted',
  options: { fallbackFilename?: string; format?: 'pdf' } = {}
): Promise<Blob> {
  const { fallbackFilename, format } = options;
  let resolved: { url: string; presigned: boolean } | null = null;
  try {
    resolved = await documentsApi.resolveDownloadUrl(documentId, version, format);
  } catch {
    // Fall through to the proxied blob download below.
  }

  if (resolved?.presigned) {
    const res = await fetch(resolved.url);
    if (!res.ok) throw new Error(`Failed to fetch document (${res.status})`);
    return res.blob();
  }

  const { blob } = await documentsApi.downloadDocument(documentId, version, fallbackFilename, format);
  return blob;
}

// Map one server suggestion (snake_case API shape) to the local EntitySuggestion.
// Shared by the /suggestions reload and the file-rule create response (piece D)
// so both produce identical rows for reconcileSuggestions.
function mapApiSuggestion(s: EntitySuggestionsResponse['suggestions'][number]): EntitySuggestion {
  return {
    entityId: s.entity_id,
    index: s.index,
    entityType: s.entity_type,
    text: s.text,
    confidence: s.confidence,
    page: s.page,
    bounds: s.bounds,
    replacementText: s.replacement_text,
    rationale: s.rationale,
    status: s.status,
    // Include source field for manual vs AI detection
    source: s.source,
    dictionarySource: s.dictionary_source,
    dictionaryAction: s.dictionary_action,
    manuallyOverridden: s.manually_overridden,
    startPos: s.start_pos,
    groupKey: s.group_key,
    groupLabel: s.group_label,
    version: s.version,
  };
}

// entity_id -> row version map for a mutation's compare-and-set: the server
// skips (and 409s) any id whose row changed since the version we last read.
// Rows without a known version (optimistic temp rows) are simply omitted —
// they keep last-write-wins.
function buildEntityVersions(rows: EntitySuggestion[], ids: string[]): Record<string, number> {
  const byId = new Map(rows.map((r) => [r.entityId, r.version] as const));
  const versions: Record<string, number> = {};
  for (const id of ids) {
    const version = byId.get(id);
    if (typeof version === 'number') versions[id] = version;
  }
  return versions;
}

// A 409 from a versioned mutation: some rows changed since we read them. The
// server applied the non-conflicted ids and skipped the rest.
function isVersionConflict(err: unknown): boolean {
  return (err as { response?: { status?: number } } | null)?.response?.status === 409;
}

// The status an optimistic edit is driving an entity toward, before the server
// has confirmed it. Tracked per-entityId so a reconcile can protect the user's
// intent from a stale refetch.
type PendingStatus = EntitySuggestion['status'];

// Phase of the single per-document rescan dot in the sidebar.
type RescanDotPhase = 'spin' | 'check' | 'x';
// How long a finished tick/X lingers before it's removed (fades out via CSS).
const RESCAN_DOT_FADE_MS = 1200;
// Safety net: if an optimistically-seeded spinner is never matched to a polled
// `rescanning` job (the rescan finished between two polls), resolve it to a tick
// after this long rather than spinning forever.
const RESCAN_DOT_FALLBACK_MS = 15000;

// Reconcile a fresh server suggestion list into the local one in place — used for
// EVERY refetch (live-preview poll AND the authoritative reload after a rule
// apply), so we never wholesale-replace the panel (selection + scroll survive).
//
// Two things it protects, both reported as bugs:
//   1. Reset — server rows merge by entityId, so the list isn't rebuilt and the
//      user's place in it is kept.
//   2. Race — an entity with an unconfirmed optimistic edit keeps its LOCAL row
//      until a server response actually reports the intended status. So a redact
//      clicked right after "Redact for file" (whose refetch is already in flight)
//      can't be reverted by that older response. Once the server catches up, the
//      pending marker is dropped and the authoritative row wins.
// Local-only optimistic adds (manual entities with temp ids the server hasn't
// returned yet) are preserved while still pending.
// Value-equality for the fields that affect how a suggestion renders (in the
// results panel row AND the PDF overlay). Lets reconcile reuse the previous row
// object when a refetch returns identical data, so memoized children skip.
function suggestionsEqual(a: EntitySuggestion, b: EntitySuggestion): boolean {
  if (a === b) return true;
  const ab = a.bounds;
  const bb = b.bounds;
  const boundsEqual = ab === bb
    || (ab != null && bb != null && ab.x === bb.x && ab.y === bb.y && ab.width === bb.width && ab.height === bb.height);
  return (
    boundsEqual &&
    a.status === b.status &&
    a.replacementText === b.replacementText &&
    a.page === b.page &&
    a.entityType === b.entityType &&
    a.text === b.text &&
    a.rationale === b.rationale &&
    a.groupKey === b.groupKey &&
    a.groupLabel === b.groupLabel &&
    a.confidence === b.confidence &&
    a.source === b.source &&
    a.dictionarySource === b.dictionarySource &&
    a.dictionaryAction === b.dictionaryAction &&
    a.manuallyOverridden === b.manuallyOverridden &&
    a.textPending === b.textPending &&
    a.isOptimistic === b.isOptimistic &&
    a.index === b.index &&
    // Not user-visible, but a reused stale row would echo a stale version on
    // the next mutation and 409 spuriously — a bumped version must win the ref.
    a.version === b.version
  );
}

function reconcileSuggestions(
  local: EntitySuggestion[],
  incoming: EntitySuggestion[],
  pending: Map<string, PendingStatus>,
): EntitySuggestion[] {
  const localById = new Map(local.map((s) => [s.entityId, s] as const));
  // Reuse the previous row object when the server row is value-equal, so an
  // unchanged refetch doesn't hand React a new reference (memoized panel rows and
  // PDF overlays then skip re-rendering).
  const keepStableRef = (server: EntitySuggestion): EntitySuggestion => {
    const prev = localById.get(server.entityId);
    return prev && suggestionsEqual(prev, server) ? prev : server;
  };
  const merged = incoming.map((server) => {
    const intended = pending.get(server.entityId);
    if (intended === undefined) return keepStableRef(server);
    if (server.status === intended) {
      pending.delete(server.entityId); // server caught up — accept its row
      return keepStableRef(server);
    }
    return localById.get(server.entityId) ?? server; // keep optimistic row
  });
  const incomingIds = new Set(incoming.map((s) => s.entityId));
  for (const s of local) {
    if (!incomingIds.has(s.entityId) && pending.has(s.entityId)) merged.push(s);
  }
  // Whole-list short-circuit: if every row is reference-equal to the previous list
  // (same length, same order), return `local` so setState is a no-op and React
  // skips the entire downstream render.
  if (merged.length === local.length && merged.every((s, i) => s === local[i])) {
    return local;
  }
  return merged;
}

// Project the per-document progress the server already reduced (doc.progress,
// present when the case is fetched with include_progress=true) into the shapes
// the sidebar consumes. The reduction rules — newest-active job for progress/ETA,
// newest result-bearing job for counts, newest job for the failed flag — now live
// server-side in RedactionJob.get_progress_by_documents_batch.
function buildEntityProgress(documents: CaseDocument[]) {
  const progressMap = new Map<string, { detected: number; approved: number; rejected: number }>();
  const processing = new Set<string>();
  const failed = new Set<string>();
  // Failure reason per failed doc (when the server surfaced one), for the sidebar.
  const failureReasons = new Map<string, string>();
  const progressByDoc = new Map<string, number>();
  // Per-document time estimate (seconds until done) for the active job, when known.
  const etaByDoc = new Map<string, number>();
  // Docs whose active job is a bulk-redaction rescan (vs an initial scan). The
  // sidebar shows a single rescan dot for these instead of the "Analyzing…" bar.
  // They stay in `processing` too, so the progress/preview polls keep running.
  const rescanning = new Set<string>();

  for (const doc of documents) {
    const p = doc.progress;
    if (!p) continue;

    if (p.is_failed) {
      failed.add(doc.id);
      if (p.error_message) failureReasons.set(doc.id, p.error_message);
    }

    if (p.is_processing) {
      processing.add(doc.id);
      progressByDoc.set(doc.id, p.progress ?? 0);
      if (p.estimated_seconds_remaining != null) {
        etaByDoc.set(doc.id, p.estimated_seconds_remaining);
      }
      if (p.active_status === 'rescanning') rescanning.add(doc.id);
    }

    if (p.has_results) {
      progressMap.set(doc.id, {
        detected: p.entities_detected,
        approved: p.entities_approved,
        rejected: p.entities_rejected,
      });
    }
  }

  return { progressMap, processing, progressByDoc, etaByDoc, failed, failureReasons, rescanning };
}

// Signatures used by the 3s poll to skip re-rendering when nothing the sidebar
// shows actually changed — otherwise every tick replaces the documents/case
// references and re-renders the whole tree (and could clobber an optimistic row
// update with identical server data).
function documentsSignature(documents: CaseDocument[]): string {
  return documents
    .map((d) => {
      const p = d.progress;
      const prog = p
        ? `${p.is_processing}:${p.is_failed}:${p.progress}:${p.estimated_seconds_remaining ?? ''}:${p.has_results}:${p.entities_detected}:${p.entities_approved}:${p.entities_rejected}:${p.active_status ?? ''}:${p.error_message ?? ''}`
        : '';
      return `${d.id}:${d.status}:${d.children_count ?? 0}:${d.is_container ?? false}:${prog}`;
    })
    .join('|');
}

function caseSignature(c: Case | null): string {
  if (!c) return '';
  return `${c.id}:${c.name}:${c.status}:${c.notes ?? ''}`;
}

export function CaseEditorPage() {
  const navigate = useNavigate();
  const { caseId } = useParams<{ caseId: string }>();

  // Case state
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [documents, setDocuments] = useState<CaseDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Document viewer state
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const currentDocument = useMemo(
    () => documents.find((document) => document.id === selectedDocumentId) ?? null,
    [documents, selectedDocumentId],
  );
  const [pdfData, setPdfData] = useState<PdfSource | null>(null);
  // Un-redacted source for the "Original" pane of the split view. Only populated
  // for finalized docs, whose pdfData is the burned-in redacted version.
  const [originalPdfData, setOriginalPdfData] = useState<PdfSource | null>(null);
  const [htmlContent, setHtmlContent] = useState<HtmlContent | null>(null);
  const [contentDocumentId, setContentDocumentId] = useState<string | null>(null);
  const [documentError, setDocumentError] = useState<string | null>(null);
  const [isFinalizingPst, setIsFinalizingPst] = useState(false);
  const contentLoadIdRef = useRef(0);

  // Panel state
  const [isDocumentsPanelCollapsed, setIsDocumentsPanelCollapsed] = useState(false);
  const {
    dockPosition,
    setDockPosition,
    isCollapsed: isResultsPanelCollapsed,
    toggleCollapse: toggleResultsPanelCollapse,
    panelSize,
    isDragging: isResultsPanelDragging,
    handleMouseDown: handleResultsPanelMouseDown,
    panelRef: resultsPanelRef,
  } = useResultsPanelDock();

  // Entity suggestions state
  const [suggestions, setSuggestionsState] = useState<EntitySuggestion[]>([]);
  const [suggestionsDocumentId, setSuggestionsDocumentId] = useState<string | null>(null);
  const [loadingSuggestionsDocumentId, setLoadingSuggestionsDocumentId] = useState<string | null>(null);
  const suggestionsRef = useRef<EntitySuggestion[]>(suggestions);
  suggestionsRef.current = suggestions;
  // Tracks which document the current suggestions belong to (set when suggestions are assigned)
  const suggestionsDocIdRef = useRef<string | null>(null);
  // Tracks which document we're currently loading suggestions for (set when loading starts)
  const loadingDocIdRef = useRef<string | null>(null);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  // Optimistic edits awaiting server confirmation (entityId → intended status).
  // reconcileSuggestions reads this so a stale refetch can't revert a just-made
  // edit. Cleared on confirm (inside reconcile), on rollback, and on doc switch.
  const pendingMutationsRef = useRef<Map<string, PendingStatus>>(new Map());
  // Monotonic guard so an older in-flight /suggestions response can't overwrite a
  // newer one (the 3s live-preview poll and a rule-apply reload can overlap).
  const fetchSeqRef = useRef(0);
  const appliedFetchSeqRef = useRef(0);
  // Delta-poll cursor: the update_seq of the last APPLIED /suggestions response
  // and the job it belongs to. The background poll passes it as `since` so the
  // server ships only rows written after it (plus the full id list for delete
  // reconciliation). Null forces the next fetch to be a full one — reset on doc
  // switch, on errors, and after a 409 (the conflict proves our view is stale).
  const suggestionsCursorRef = useRef<{ jobId: string; seq: number } | null>(null);
  const markPending = useCallback((ids: string[], status: PendingStatus) => {
    ids.forEach((id) => pendingMutationsRef.current.set(id, status));
  }, []);
  const clearPending = useCallback((ids: string[]) => {
    ids.forEach((id) => pendingMutationsRef.current.delete(id));
  }, []);
  // Holds the selected entity's stable entityId (not an array index).
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState<string | null>(null);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);
  // Status of the job whose suggestions are currently loaded. When this is a
  // pipeline phase (queued/processing/rescanning) the loaded suggestions are a
  // LIVE PREVIEW of pages scanned so far. Every page stays editable during the
  // preview — pages not yet scanned just have no suggestions drawn yet. See
  // live-page-preview design note.
  const [loadedJobStatus, setLoadedJobStatus] = useState<string | null>(null);
  const [loadedReadyPages, setLoadedReadyPages] = useState<number[] | null>(null);
  // Page-readiness of the loaded live preview: how many pages have been scanned
  // (ready_pages) out of the document total. Used to detect the convergence
  // window — all pages scanned but the job still `processing` because the final
  // grouping/re-sort pass hasn't published grouped results yet. Both null once
  // the job is past processing (the response omits them when not relevant).
  const [loadedReadyPageCount, setLoadedReadyPageCount] = useState<number | null>(null);
  const [loadedTotalPageCount, setLoadedTotalPageCount] = useState<number | null>(null);
  const visibleSuggestions = suggestionsDocumentId === selectedDocumentId
    ? suggestions
    : EMPTY_SUGGESTIONS;
  // Live preview is active when the selected document's loaded suggestions belong
  // to a still-running INITIAL scan (processing/queued). The pipeline only writes
  // entities for pages it has finished, so every served suggestion is on a "ready"
  // page and we reveal them page-by-page with a "Scanned X/Y" banner.
  //
  // A `rescanning` job is deliberately NOT a live preview: the document was already
  // fully scanned, so we show it normally (all pages, normal toolbar, editable) and
  // surface the rescan only as the single sidebar dot. Including `rescanning` here
  // would re-show the in-viewer banner + page-reveal gating the dot replaces.
  const isLivePreview = suggestionsDocumentId === selectedDocumentId
    && (loadedJobStatus === 'processing' || loadedJobStatus === 'queued');
  // Convergence window: every page has been scanned but the job is still
  // `processing`, so the final pass (global grouping + deterministic re-sort) is
  // about to run. That pass re-keys the whole list — entities get group_key and a
  // new order — which would otherwise read as a flicker mid-review. Surfacing an
  // explicit "finalizing" state makes the collapse-into-groups look intentional.
  // `isLivePreview` is already initial-scan-only, so a rescan can't enter this.
  const isScanFinalizing = isLivePreview
    && loadedTotalPageCount !== null
    && loadedTotalPageCount > 0
    && loadedReadyPageCount !== null
    && loadedReadyPageCount >= loadedTotalPageCount;
  const isLoadingSelectedSuggestions = isLoadingSuggestions
    && loadingSuggestionsDocumentId === selectedDocumentId;
  // Pages reveal in contiguous order during a live preview: a page only counts
  // as ready once every page before it is ready too. The pipeline can finish
  // pages out of order (parallel OCR workers), but revealing page 10's entities
  // while page 7 is still scanning is confusing — so we reveal up to the first
  // gap and treat the rest as still scanning (page 10 waits for 8 and 9).
  const contiguousReadyPages = useMemo(() => {
    if (!loadedReadyPages) return null;
    const readySet = new Set(loadedReadyPages);
    const revealed: number[] = [];
    for (let page = 0; readySet.has(page); page++) revealed.push(page);
    return revealed;
  }, [loadedReadyPages]);

  // All editor mutations are scoped to the document that created the callback.
  // An async completion from a previous document becomes a no-op after switching.
  const setSuggestions = useCallback((
    update: EntitySuggestion[] | ((previous: EntitySuggestion[]) => EntitySuggestion[]),
  ) => {
    const ownerDocumentId = selectedDocumentId;
    if (!ownerDocumentId) return;
    setSuggestionsState((previous) => {
      if (suggestionsDocIdRef.current !== ownerDocumentId) return previous;
      return typeof update === 'function' ? update(previous) : update;
    });
  }, [selectedDocumentId]);

  // `background` is set for the live-preview refresh poll (every 3s). A background
  // refresh must be SILENT: it swaps in newly-scanned entities without toggling
  // the loading flag, dropping the user's selection, or nulling the job id. Doing
  // those on every tick made the results panel open/close on each poll when a doc
  // had 0 entities so far (panel visibility keys off the loading flag), and reset
  // any active selection mid-preview.
  const loadSuggestionsForDocument = useCallback(async (
    documentId: string,
    { background = false }: { background?: boolean } = {},
  ) => {
    loadingDocIdRef.current = documentId;
    // Switching to a DIFFERENT document than the one whose suggestions are
    // currently loaded: drop the previous doc's pending optimistic mutations.
    // pendingMutationsRef is keyed by entityId (per-document) and exists only to
    // survive a reload of the SAME doc (live-preview poll / rescan reload). If it
    // carried across a switch, reconcileSuggestions' tail loop would re-inject the
    // old doc's pending rows — with the old doc's page/bounds — into the new doc's
    // list and draw them as stale highlights. The clicked entity's real status is
    // already persisted server-side, so reloading the old doc later still reflects
    // it. Same-doc reloads (background or not) keep the pending map intact.
    if (documentId !== suggestionsDocIdRef.current) {
      pendingMutationsRef.current.clear();
      suggestionsCursorRef.current = null;
    }
    // Order this fetch so a slower, older response can't land after a newer one.
    const fetchSeq = ++fetchSeqRef.current;
    if (!background) {
      setLoadingSuggestionsDocumentId(documentId);
      setIsLoadingSuggestions(true);
      // Don't clear suggestions here — keep showing previous data until new
      // suggestions arrive to prevent the progress bar from flickering to 0%
      setSelectedSuggestionIndex(null);
      setCurrentJobId(null);
    }

    try {
      // Load the newest job that actually carries reviewable results, using the
      // SAME status set as buildEntityProgress(). Filtering to only
      // 'awaiting_approval' here could grab a stale older job (or skip a newer
      // 'approved'/'completed' one), so the suggestions-sync effect would then
      // overwrite the sidebar progress bar with that stale job's counts — making
      // the count flip to a wrong value the moment a document is opened.
      const jobs = await redactionApi.getJobs({ document_id: documentId });

      // Guard against stale responses if user switched documents while loading
      if (documentId !== loadingDocIdRef.current) return;

      // jobs are returned newest-first, so the first result-bearing job is current.
      // Fall back to a still-running job (including a `rescanning` one) so we can
      // show its live page preview (entities for pages scanned so far) while the
      // scan/rescan is in flight. Last resort: a `failed` job — automatic detection
      // never produced results, but the uploaded document is still renderable and
      // the user must be able to redact it manually (draw blackout boxes / add
      // manual entities), which needs a job id to attach those entities to.
      const job = jobs.find((j) => RESULT_STATUSES.has(j.status))
        ?? jobs.find((j) => j.status === 'processing' || j.status === 'queued' || j.status === 'rescanning')
        ?? jobs.find((j) => j.status === 'failed');
      if (!job) {
        suggestionsDocIdRef.current = documentId;
        setSuggestionsDocumentId(documentId);
        pendingMutationsRef.current.clear();
        suggestionsCursorRef.current = null;
        setSuggestionsState([]);
        setLoadedJobStatus(null);
        setLoadedReadyPages(null);
        setLoadedReadyPageCount(null);
        setLoadedTotalPageCount(null);
        return;
      }
      setCurrentJobId(job.job_id);

      // Delta poll: only the silent background refresh reuses the cursor, and
      // only while it still points at the job whose rows are on screen — a doc
      // switch, job change, or explicit reload always takes the full fetch.
      const cursor = suggestionsCursorRef.current;
      const useDelta =
        background && cursor !== null && cursor.jobId === job.job_id && documentId === suggestionsDocIdRef.current;

      // Load suggestions — pass case_id so the server applies dictionary matching
      const suggestionsResponse = await redactionApi.getSuggestions(job.job_id, {
        case_id: caseId,
        includeRejected: true,
        since: useDelta ? cursor.seq : undefined,
      });
      // Check again after async call
      if (documentId !== loadingDocIdRef.current) return;

      // Drop this response if a newer fetch has already been applied.
      if (fetchSeq < appliedFetchSeqRef.current) return;
      appliedFetchSeqRef.current = fetchSeq;
      if (suggestionsResponse.delta && !suggestionsResponse.entity_ids) {
        // Unchanged short-circuit: the server's write cursor hasn't moved past
        // our `since`, so nothing changed and no ids were shipped — the local
        // list is already authoritative. Refresh only the cursor and the job
        // status fields (the poll still tracks scan progress through them).
        suggestionsCursorRef.current = { jobId: job.job_id, seq: suggestionsResponse.update_seq ?? 0 };
        setLoadedJobStatus(suggestionsResponse.job_status);
        setLoadedReadyPages(suggestionsResponse.ready_pages ?? null);
        setLoadedReadyPageCount(suggestionsResponse.ready_pages?.length ?? null);
        setLoadedTotalPageCount(suggestionsResponse.total_pages ?? null);
        return;
      }
      const mappedChanged = suggestionsResponse.suggestions.map(mapApiSuggestion);
      let mappedSuggestions = mappedChanged;
      if (suggestionsResponse.delta && suggestionsResponse.entity_ids) {
        // Reconstruct the server's full list from the delta: entity_ids carries
        // the authoritative membership + order, changed rows are authoritative
        // content, every other id keeps its local row (ids missing from both —
        // shouldn't happen, a new row always carries a fresh seq — mean our
        // base is stale: drop the cursor so the next poll refetches full).
        const changedById = new Map(mappedChanged.map((s) => [s.entityId, s] as const));
        const prevById = new Map(suggestionsRef.current.map((s) => [s.entityId, s] as const));
        const resolved: EntitySuggestion[] = [];
        let missingBase = false;
        for (const id of suggestionsResponse.entity_ids) {
          const row = changedById.get(id) ?? prevById.get(id);
          if (!row) {
            missingBase = true;
            break;
          }
          resolved.push(row);
        }
        if (missingBase) {
          suggestionsCursorRef.current = null;
          return;
        }
        mappedSuggestions = resolved;
      }
      suggestionsDocIdRef.current = documentId;
      setSuggestionsDocumentId(documentId);
      // Every refetch reconciles in place — no wholesale replace. Server rows
      // merge by entityId (selection + scroll survive) and any unconfirmed
      // optimistic edit keeps its local row until the server reports the same
      // status. This is what stops a rule-apply reload from resetting the panel
      // or reverting a redaction clicked while the reload was in flight.
      setSuggestionsState((prev) =>
        reconcileSuggestions(prev, mappedSuggestions, pendingMutationsRef.current)
      );
      suggestionsCursorRef.current = { jobId: job.job_id, seq: suggestionsResponse.update_seq ?? 0 };
      setLoadedJobStatus(suggestionsResponse.job_status);
      setLoadedReadyPages(suggestionsResponse.ready_pages ?? null);
      setLoadedReadyPageCount(suggestionsResponse.ready_pages?.length ?? null);
      setLoadedTotalPageCount(suggestionsResponse.total_pages ?? null);
    } catch (err) {
      if (documentId !== loadingDocIdRef.current) return;
      console.error('Failed to load suggestions:', err);
      suggestionsDocIdRef.current = documentId;
      setSuggestionsDocumentId(documentId);
      pendingMutationsRef.current.clear();
      suggestionsCursorRef.current = null;
      setSuggestionsState([]);
      setLoadedJobStatus(null);
      setLoadedReadyPages(null);
      setLoadedReadyPageCount(null);
      setLoadedTotalPageCount(null);
    } finally {
      if (!background && documentId === loadingDocIdRef.current) {
        setIsLoadingSuggestions(false);
      }
    }
  }, [caseId]);

  // Recovery for a 409 from a versioned mutation: the server applied the
  // non-conflicted ids and skipped the rows that changed since we read them, so
  // a local rollback would be wrong on both counts — refetch the truth instead.
  // Full fetch (cursor dropped): the conflict itself proves our view is stale.
  const refreshAfterConflict = useCallback((entityIds: string[]) => {
    clearPending(entityIds);
    suggestionsCursorRef.current = null;
    const docId = suggestionsDocIdRef.current;
    if (docId) void loadSuggestionsForDocument(docId, { background: true });
  }, [clearPending, loadSuggestionsForDocument]);

  // Entity progress per document (for progress bars in side panel)
  const [entityProgress, setEntityProgress] = useState<Map<string, { detected: number; approved: number; rejected: number }>>(new Map());

  // Track documents with active (queued/processing) jobs for progress indicators
  const [processingDocumentIds, setProcessingDocumentIds] = useState<Set<string>>(new Set());

  // Documents whose active job is a bulk-redaction rescan (subset of
  // processingDocumentIds). Drives the single sidebar rescan dot; kept separate
  // so genuine initial scans still get the detailed "Analyzing…" bar.
  const [rescanningDocumentIds, setRescanningDocumentIds] = useState<Set<string>>(new Set());

  // Documents whose latest job failed — shown as a "Failed" state in the side panel
  const [failedDocumentIds, setFailedDocumentIds] = useState<Set<string>>(new Set());

  // Failure reason per failed document (when the server surfaced one), shown next
  // to the "Processing failed" state so the reviewer knows why it failed.
  const [failureReasons, setFailureReasons] = useState<Map<string, string>>(new Map());

  // Analysis progress (0-100) per actively-processing document, for the analyzing bar
  const [processingProgress, setProcessingProgress] = useState<Map<string, number>>(new Map());

  // Estimated seconds-until-done per actively-processing document, for the ETA label
  const [processingEta, setProcessingEta] = useState<Map<string, number>>(new Map());

  // Single per-document rescan indicator (replaces the old panel "Rescanning…"
  // pill, the sidebar rescan progress bar, and the in-viewer banner). A row shows
  // one dot: spinning while a bulk-redaction rescan is in flight, then a tick on
  // success or an X on error, which fades out. Owned here because only this page
  // knows the action scope (which doc the user just acted on). Map values:
  //   'spin'  — rescan in flight
  //   'check' — finished successfully (fading out)
  //   'x'     — failed (fading out)
  const [rescanDotStatus, setRescanDotStatus] = useState<Map<string, RescanDotPhase>>(new Map());
  const rescanDotStatusRef = useRef(rescanDotStatus);
  rescanDotStatusRef.current = rescanDotStatus;
  // One pending timer per doc: either a fade-out clear (after a tick/X) or the
  // optimistic-seed fallback (resolve a stuck spinner if a fast rescan finishes
  // between polls). A doc has at most one in flight, so a single map is enough.
  const rescanDotTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Docs the poll has actually reported `rescanning` for. A spinner only resolves
  // to a tick once the doc has been seen rescanning AND then leaves that state, so
  // a not-yet-started (queued) job can't be mistaken for a finished one.
  const observedRescanningRef = useRef<Set<string>>(new Set());
  // Docs whose active job is still an INITIAL scan (queued/processing), not a
  // rescan. A bulk-redaction action on such a doc must keep its detailed
  // "Analyzing…" progress bar rather than swap in a rescan dot: the server won't
  // claim a pipeline-owned job for a rescan (it excludes pending/queued/processing),
  // so the action's new rules are simply reconciled when the in-flight scan
  // finishes — there is no separate rescan to spin for. Read by seedRescanDot to
  // skip these docs. Kept current by the progress-derive effect below.
  const initialScanDocIdsRef = useRef<Set<string>>(new Set());

  const clearRescanDotTimer = useCallback((docId: string) => {
    const t = rescanDotTimersRef.current.get(docId);
    if (t) {
      clearTimeout(t);
      rescanDotTimersRef.current.delete(docId);
    }
  }, []);
  const setRescanDotPhase = useCallback((docId: string, phase: RescanDotPhase | null) => {
    setRescanDotStatus((prev) => {
      if (phase === null) {
        if (!prev.has(docId)) return prev;
        const next = new Map(prev);
        next.delete(docId);
        return next;
      }
      if (prev.get(docId) === phase) return prev;
      const next = new Map(prev);
      next.set(docId, phase);
      return next;
    });
  }, []);
  // Hold a finished tick/X briefly, then remove it (the dot fades out via CSS).
  const scheduleRescanDotClear = useCallback((docId: string) => {
    clearRescanDotTimer(docId);
    const t = setTimeout(() => {
      rescanDotTimersRef.current.delete(docId);
      setRescanDotPhase(docId, null);
    }, RESCAN_DOT_FADE_MS);
    rescanDotTimersRef.current.set(docId, t);
  }, [clearRescanDotTimer, setRescanDotPhase]);
  // Optimistic spin on the doc the user just acted on, so feedback is instant
  // rather than waiting for the worker to claim the job + the next poll. The poll
  // reconcile (effect below) then owns the spin→tick transition; the fallback
  // timer only fires if the rescan never shows up in a poll (finished too fast).
  const seedRescanDot = useCallback((docId: string | null) => {
    if (!docId) return;
    // A doc still in its initial scan keeps its "Analyzing…" progress bar — the
    // action it just triggered can't kick off a rescan until that scan finishes
    // (the server won't claim a pipeline-owned job), so a dot here would only hide
    // the progress bar behind a spinner that resolves on the fallback timer.
    if (initialScanDocIdsRef.current.has(docId)) return;
    clearRescanDotTimer(docId);
    setRescanDotPhase(docId, 'spin');
    const t = setTimeout(() => {
      rescanDotTimersRef.current.delete(docId);
      if (rescanDotStatusRef.current.get(docId) === 'spin') {
        setRescanDotPhase(docId, 'check');
        scheduleRescanDotClear(docId);
      }
    }, RESCAN_DOT_FALLBACK_MS);
    rescanDotTimersRef.current.set(docId, t);
  }, [clearRescanDotTimer, setRescanDotPhase, scheduleRescanDotClear]);
  const failRescanDot = useCallback((docId: string | null) => {
    if (!docId) return;
    // Mirror seedRescanDot: a doc mid initial-scan never started a rescan dot, so
    // there is nothing to flip to an X — leave its progress bar in place.
    if (initialScanDocIdsRef.current.has(docId)) return;
    observedRescanningRef.current.delete(docId);
    clearRescanDotTimer(docId);
    setRescanDotPhase(docId, 'x');
    scheduleRescanDotClear(docId);
  }, [clearRescanDotTimer, setRescanDotPhase, scheduleRescanDotClear]);

  // Clear all pending dot timers on unmount.
  useEffect(() => {
    const timers = rescanDotTimersRef.current;
    return () => timers.forEach((t) => clearTimeout(t));
  }, []);

  // Reasons state
  const { reasons } = useReasons();
  const [entityReasons, setEntityReasons] = useState<Map<string, string>>(new Map());

  // Upload modal state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  // Sidebar-dropped files whose names already exist in the case, waiting for
  // the user to confirm re-upload via DuplicateUploadDialog.
  const [pendingDuplicateDrop, setPendingDuplicateDrop] = useState<File[]>([]);

  // Delete case state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeletingCase, setIsDeletingCase] = useState(false);

  // Delete document state
  const [deleteDocumentTarget, setDeleteDocumentTarget] = useState<string | null>(null);
  const [reprocessTarget, setReprocessTarget] = useState<string | null>(null);
  const [isDeletingDocument, setIsDeletingDocument] = useState(false);

  // Download/export loading state
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  // Per-action loading state for the finalized-document toolbar (print/download/edit).
  const [isPrinting, setIsPrinting] = useState(false);
  const [isUnfinalizing, setIsUnfinalizing] = useState(false);
  // Tracks which download option is in flight ('original' | 'redacted' | 'redacted-pdf').
  const [downloadingVersion, setDownloadingVersion] = useState<string | null>(null);
  // Per-document download spinners in the documents list, keyed by `${documentId}:${version}`.
  const [downloadingDocIds, setDownloadingDocIds] = useState<Set<string>>(new Set());

  // Dictionary modal state
  const [isCaseDictModalOpen, setIsCaseDictModalOpen] = useState(false);
  const [fileDictDocId, setFileDictDocId] = useState<string | null>(null);

  // Preload the PDFEditor chunk in parallel with the case fetch, so the editor
  // shell (toolbar included) mounts synchronously when the first document
  // auto-selects instead of waiting on the lazy import behind the Suspense.
  useEffect(() => {
    void importPDFEditor();
  }, []);

  // Load case data
  useEffect(() => {
    if (!caseId) return;

    const loadCase = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // One request: the case plus its per-document redaction progress, so the
        // sidebar bars come down with the document list and we don't need a second
        // round-trip for the full job list. The progress maps are derived from
        // `documents` by the effect below (single source of truth).
        const response = await casesApi.getCase(caseId, { includeProgress: true });
        setCaseData(response);
        setDocuments(response.documents);

        // Auto-select first document if available
        if (response.documents.length > 0) {
          setSelectedDocumentId(response.documents[0].id);
        }
      } catch (err) {
        console.error('Failed to load case:', err);
        setError(apiHelpers.handleError(err));
      } finally {
        setIsLoading(false);
      }
    };

    loadCase();
  }, [caseId]);

  // Refresh the case and its embedded per-document progress in one request.
  // Picks up document changes (e.g. container children appearing) and refreshes
  // the sidebar progress bars (the effect below re-derives them from `documents`).
  // Called after redaction actions to reconcile with the server and on the poll
  // below while work is live. Bails out of the state update when nothing the
  // sidebar shows changed, so the 3s poll doesn't churn renders or revert an
  // optimistic row update with identical server data.
  // Collapse overlapping refreshes: a burst of actions (or a poll tick landing
  // while a fetch is in flight) must not stack N identical case+progress
  // requests — this response is ~50KB on a large case and one in-flight fetch
  // already carries the freshest state either caller could get.
  const entityProgressInFlightRef = useRef(false);
  const loadEntityProgress = useCallback(async () => {
    if (!caseId || entityProgressInFlightRef.current) return;
    entityProgressInFlightRef.current = true;
    try {
      const response = await casesApi.getCase(caseId, { includeProgress: true });
      const nextSig = documentsSignature(response.documents);
      setDocuments((prev) => (documentsSignature(prev) === nextSig ? prev : response.documents));
      setCaseData((prev) => (caseSignature(prev) === caseSignature(response) ? prev : response));
    } catch (err) {
      console.error('Failed to load entity progress:', err);
    } finally {
      entityProgressInFlightRef.current = false;
    }
  }, [caseId]);

  // A case/global dictionary rule enqueues its rescan as a background task AFTER the
  // HTTP response, so jobs flip to `rescanning` a beat later. Refresh progress a few
  // times so the sidebar rescan dots appear as the jobs flip; the first refresh that
  // sees a rescanning job adds it to processingDocumentIds, which starts the
  // self-sustaining 3s poll, so this only needs to bridge the initial gap.
  // A FILE-scope rule claims its one job synchronously inside the request, so the
  // immediate refresh already sees it `rescanning` — no bridging timers needed
  // (they used to cost 3 extra ~50KB case+progress fetches per redact click).
  const dictRescanPollTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const watchForRescans = useCallback((scope: 'file' | 'case' | 'global' = 'case') => {
    dictRescanPollTimersRef.current.forEach(clearTimeout);
    loadEntityProgress();
    dictRescanPollTimersRef.current = scope === 'file'
      ? []
      : [1500, 3500, 6000].map((ms) => setTimeout(loadEntityProgress, ms));
  }, [loadEntityProgress]);
  useEffect(() => {
    const timers = dictRescanPollTimersRef;
    return () => timers.current.forEach(clearTimeout);
  }, []);

  // Single source of truth for the sidebar progress maps: derive them from the
  // embedded per-document progress whenever `documents` changes (initial load,
  // poll, or a post-action refresh). Every path that updates `documents` fetches
  // with includeProgress, so the embedded progress is always present here.
  useEffect(() => {
    const { progressMap, processing, progressByDoc, etaByDoc, failed, failureReasons, rescanning } = buildEntityProgress(documents);
    setEntityProgress(progressMap);
    setProcessingDocumentIds(processing);
    setProcessingProgress(progressByDoc);
    setProcessingEta(etaByDoc);
    setFailedDocumentIds(failed);
    setFailureReasons(failureReasons);
    setRescanningDocumentIds(rescanning);
    // Docs in an initial scan = active (processing) but not rescanning. seedRescanDot
    // reads this to keep their progress bar instead of swapping in a rescan dot.
    const initialScans = new Set<string>();
    processing.forEach((id) => { if (!rescanning.has(id)) initialScans.add(id); });
    initialScanDocIdsRef.current = initialScans;
  }, [documents]);

  // Reconcile the rescan dots against polled truth. Reads the current phases from
  // a ref (not the deps) so it only re-runs when the polled rescanning/failed sets
  // change — and so its own setState can't loop. Two passes:
  //   1. Any doc the poll reports rescanning starts (or keeps) spinning; cancel its
  //      fallback timer since the rescan is now confirmed in flight.
  //   2. Any spinning doc that has LEFT the rescanning set after being seen in it
  //      resolves to a tick; a doc whose latest job failed resolves to an X.
  useEffect(() => {
    const phases = rescanDotStatusRef.current;
    rescanningDocumentIds.forEach((docId) => {
      observedRescanningRef.current.add(docId);
      clearRescanDotTimer(docId);
      if (phases.get(docId) !== 'spin') setRescanDotPhase(docId, 'spin');
    });
    phases.forEach((phase, docId) => {
      if (phase !== 'spin') return;
      if (failedDocumentIds.has(docId)) {
        failRescanDot(docId);
      } else if (!rescanningDocumentIds.has(docId) && observedRescanningRef.current.has(docId)) {
        observedRescanningRef.current.delete(docId);
        clearRescanDotTimer(docId);
        setRescanDotPhase(docId, 'check');
        scheduleRescanDotClear(docId);
      }
    });
  }, [rescanningDocumentIds, failedDocumentIds, clearRescanDotTimer, setRescanDotPhase, scheduleRescanDotClear, failRescanDot]);

  // A container (PST/ZIP) whose children haven't been extracted yet: the worker
  // unpacks them after upload, so the case initially loads with the container but
  // zero children. Reload while one exists so the children — and the redaction
  // jobs the ingest job queues for them — appear without a manual refresh.
  const hasExtractingContainer = useMemo(
    () => documents.some((d) => d.is_container && (d.children_count ?? 0) === 0),
    [documents]
  );

  // Single poll while anything is in flight: active redaction jobs OR a container
  // still being extracted. Both now resolve from the same case+progress request,
  // so one interval covers what used to be two separate polls (and the global
  // job-list fetch is gone entirely).
  useEffect(() => {
    if (processingDocumentIds.size === 0 && !hasExtractingContainer) return;
    const interval = setInterval(loadEntityProgress, 3000);
    return () => clearInterval(interval);
  }, [processingDocumentIds.size, hasExtractingContainer, loadEntityProgress]);

  // Reload suggestions when the selected document finishes processing/rescanning.
  // This is the single authoritative panel update at completion. Do it as a SILENT
  // background merge so it reconciles in place (keeps the user's selection + scroll,
  // no loading flash) instead of a heavy foreground reload. For a rescan this is the
  // ONLY suggestions refetch — the live-preview poll below is initial-scan only — so
  // the panel updates exactly once, on the same progress-poll tick that drops the doc
  // from processingDocumentIds and flips the rescan dot to its tick.
  const prevProcessingRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (selectedDocumentId && prevProcessingRef.current.has(selectedDocumentId) && !processingDocumentIds.has(selectedDocumentId)) {
      loadSuggestionsForDocument(selectedDocumentId, { background: true });
    }
    prevProcessingRef.current = processingDocumentIds;
  }, [processingDocumentIds, selectedDocumentId, loadSuggestionsForDocument]);

  // While the selected document is still in its INITIAL scan, re-fetch its
  // suggestions on the same cadence as the progress poll so newly-finished pages
  // appear in the live preview without the user having to reopen the document.
  //
  // Gated to initial scans, NOT rescans: a rescan runs on an already-fully-scanned
  // doc, so there is no page-by-page reveal to show. Its mid-flight fetches would be
  // a second, redundant way the panel updates (pre-recompute state) on top of the
  // completion reload above — the "updates twice, in different ways" symptom. Letting
  // only the completion reload fire keeps a rescan to a single, in-place update.
  //
  // Depend on a STABLE boolean, not the processingDocumentIds Set: the progress
  // poll replaces that Set with a fresh reference every 3s even when its contents
  // are unchanged. Listing the Set here would tear down and recreate this interval
  // on every progress tick, resetting its own 3s countdown before it can ever fire
  // — so the live preview would never refresh until the user reloaded the page.
  const isSelectedDocumentInitialScanning = selectedDocumentId
    ? processingDocumentIds.has(selectedDocumentId) && !rescanningDocumentIds.has(selectedDocumentId)
    : false;
  useEffect(() => {
    if (!selectedDocumentId || !isSelectedDocumentInitialScanning) return;
    const interval = setInterval(() => loadSuggestionsForDocument(selectedDocumentId, { background: true }), 3000);
    return () => clearInterval(interval);
    // loadSuggestionsForDocument is intentionally excluded: it is memoized on
    // caseId (stable for this view), and listing it would only add noise without
    // changing when the 3s poll should restart (selected doc / scanning state).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDocumentId, isSelectedDocumentInitialScanning]);

  // Keep entityProgress in sync with local suggestions for the selected document
  // so the left sidebar progress bars update immediately without waiting for server.
  // Only update when suggestions actually belong to the selected document to avoid
  // briefly writing stale data from the previous document during a switch.
  useEffect(() => {
    if (!selectedDocumentId || suggestions.length === 0) return;
    if (suggestionsDocIdRef.current !== selectedDocumentId) return;
    setEntityProgress(prev => {
      const newMap = new Map(prev);
      newMap.set(selectedDocumentId, {
        detected: suggestions.length,
        approved: suggestions.filter(s => s.status === 'approved').length,
        rejected: suggestions.filter(s => s.status === 'rejected').length,
      });
      return newMap;
    });
  }, [suggestions, selectedDocumentId]);

  // Get child documents for the current document (used for PST containers)
  const childDocuments = useMemo(() => {
    if (!currentDocument) return [];
    return documents.filter((doc) => doc.parent_id === currentDocument.id);
  }, [documents, currentDocument]);

  // Check if current document is a container (PST or ZIP)
  const isContainerDocument = useMemo(() => {
    if (!currentDocument) return false;
    return currentDocument.is_container ||
      (currentDocument.metadata?.is_container && childDocuments.length > 0);
  }, [currentDocument, childDocuments]);

  // Check if current document is an email (EML/MSG) with children
  const isEmailWithChildren = useMemo(() => {
    if (!currentDocument) return false;
    const mimeType = currentDocument.type;
    const emailMimeTypes = ['message/rfc822', 'application/vnd.ms-outlook'];
    return emailMimeTypes.includes(mimeType) && childDocuments.length > 0;
  }, [currentDocument, childDocuments]);

  // Check if all child documents are completed
  const allChildrenCompleted = useMemo(() => {
    if (childDocuments.length === 0) return true;
    return childDocuments.every((doc) => doc.status === 'completed');
  }, [childDocuments]);

  // Load selected document.
  //
  // `documents` is only a lookup source here: loads are keyed on the selected
  // document id and must NOT re-run when the list identity changes (status
  // refreshes, polling). Re-running would refetch the PDF mid-view and start a
  // duplicate suggestions load whose stale all-pending response can land after
  // a user's approve/reject and silently clobber the optimistic update.
  const loadsStartedForDocRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedDocumentId) {
      contentLoadIdRef.current += 1;
      loadsStartedForDocRef.current = null;
      suggestionsDocIdRef.current = null;
      loadingDocIdRef.current = null;
      setPdfData(null);
      setOriginalPdfData(null);
      setHtmlContent(null);
      setContentDocumentId(null);
      setSuggestionsDocumentId(null);
      setLoadingSuggestionsDocumentId(null);
      pendingMutationsRef.current.clear();
      setSuggestionsState([]);
      setCurrentJobId(null);
      setEntityReasons(new Map());
      setPendingManualRedaction(null);
      pendingRegionTextRef.current = null;
      return;
    }

    if (loadsStartedForDocRef.current === selectedDocumentId) return;

    const doc = documents.find((d) => d.id === selectedDocumentId);
    if (!doc) return;

    loadsStartedForDocRef.current = selectedDocumentId;
    setPendingManualRedaction(null);
    pendingRegionTextRef.current = null;
    loadDocumentContent(doc);
    loadSuggestionsForDocument(selectedDocumentId);
  }, [selectedDocumentId, documents, loadSuggestionsForDocument]);

  const loadDocumentContent = async (doc: CaseDocument) => {
    const loadId = ++contentLoadIdRef.current;
    setDocumentError(null);
    setPdfData(null);
    setOriginalPdfData(null);
    setHtmlContent(null);
    setContentDocumentId(null);

    // Get file extension from filename (more reliable than MIME type)
    const fileType = doc.name.split('.').pop()?.toLowerCase();

    try {
      if (fileType === 'pdf') {
        const version = doc.status === 'completed' ? 'redacted' : 'original';
        // Stream via Range requests instead of downloading the whole file first:
        // PDF.js renders the first page after a few KB and lazily fetches the rest,
        // so large PDFs open near-instantly.
        //
        // Prefer a presigned S3 GET URL (browser fetches bytes directly from
        // storage, no server in the path). When that isn't available the backend
        // returns presigned=false and we fall back to the /download route, which
        // needs the auth token as a header since PDF.js bypasses the axios
        // interceptor. Range support works the same way for both.
        const token = getAuthTokensService().getCurrentCredentials()?.access_token?.token;
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : undefined;

        const resolvePdfUrl = async (v: 'original' | 'redacted') => {
          try {
            const preview = await documentsApi.getPreviewUrl(doc.id, v);
            if (preview.presigned) {
              return { url: preview.url, httpHeaders: undefined };
            }
          } catch {
            // Fall through to the backend download route below.
          }
          return { url: documentsApi.getDownloadUrl(doc.id, v), httpHeaders: authHeaders };
        };

        const primary = await resolvePdfUrl(version);
        if (loadId !== contentLoadIdRef.current) return;
        setPdfData({
          url: primary.url,
          httpHeaders: primary.httpHeaders,
          documentId: doc.id,
          version,
        });
        // For finalized docs, pdfData is the burned-in redacted version. Also
        // load the un-redacted original so the split view's "Original" pane can
        // show a true before/after comparison.
        if (version === 'redacted') {
          const original = await resolvePdfUrl('original');
          if (loadId !== contentLoadIdRef.current) return;
          setOriginalPdfData({
            url: original.url,
            httpHeaders: original.httpHeaders,
            documentId: doc.id,
            version: 'original',
          });
        }
        setContentDocumentId(doc.id);
      } else if (['docx', 'doc', 'eml', 'msg'].includes(fileType || '')) {
        const version = doc.status === 'completed' ? 'redacted' : 'original';
        const content = await documentsApi.getHtmlContent(doc.id, version);
        if (loadId !== contentLoadIdRef.current) return;
        if (!content.success) {
          throw new Error(content.error_message || 'Failed to load document content');
        }
        setHtmlContent(content);
        setContentDocumentId(doc.id);
      } else if (fileType === 'pst' || fileType === 'zip') {
        // Container files (PST, ZIP) - their children are shown in DocumentsPanel
        // No content to load for the container itself
        setContentDocumentId(doc.id);
      } else {
        // For other file types, just show a placeholder
        setDocumentError('Preview not available for this file type');
      }
    } catch (err) {
      if (loadId !== contentLoadIdRef.current) return;
      console.error('Failed to load document:', err);
      setDocumentError(apiHelpers.handleError(err));
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleRenameCase = useCallback(async (newName: string) => {
    if (!caseId) return;
    const updated = await casesApi.updateCase(caseId, { name: newName });
    setCaseData(prev => prev ? { ...prev, name: updated.name } : prev);
  }, [caseId]);

  const handleDeleteCase = async () => {
    if (!caseId) return;
    setIsDeletingCase(true);
    try {
      await casesApi.deleteCase(caseId);
      navigate('/');
    } catch (err) {
      console.error('Failed to delete case:', err);
      setIsDeletingCase(false);
    }
  };

  const handleDeleteDocument = useCallback((documentId: string) => {
    setDeleteDocumentTarget(documentId);
  }, []);

  const handleConfirmDeleteDocument = useCallback(async () => {
    if (!deleteDocumentTarget) return;
    setIsDeletingDocument(true);
    try {
      await documentsApi.deleteDocument(deleteDocumentTarget);
      setDocuments((prev) => prev.filter((d) => d.id !== deleteDocumentTarget));
      if (selectedDocumentId === deleteDocumentTarget) {
        setSelectedDocumentId(null);
      }
      setDeleteDocumentTarget(null);
    } catch (err) {
      console.error('Failed to delete document:', err);
    } finally {
      setIsDeletingDocument(false);
    }
  }, [deleteDocumentTarget, selectedDocumentId]);

  // Reprocess a single document, steered by the entities the reviewer
  // manually flagged on it. Queues an async job, then marks the doc as
  // processing so the sidebar shows "Analyzing..."; the existing 3s poll
  // (processingDocumentIds.size > 0) clears it and the completion effect
  // reloads suggestions when detection finishes.
  // Clicking the reprocess button opens an explanation dialog first; the
  // actual job is only queued once the reviewer confirms.
  const handleRequestReprocess = useCallback((documentId: string) => {
    setReprocessTarget(documentId);
  }, []);

  const handleConfirmReprocessDocument = useCallback(async (mode: ReprocessMode) => {
    if (!reprocessTarget) return;
    const documentId = reprocessTarget;
    try {
      const result = await redactionApi.reprocessDocument(documentId, mode);
      console.info(`Reprocess queued for ${documentId}: ${result.message}`);
      setProcessingDocumentIds((prev) => new Set([...prev, documentId]));
    } catch (err) {
      console.error('Failed to reprocess document:', err);
    } finally {
      setReprocessTarget(null);
    }
  }, [reprocessTarget]);

  // Retry processing for a failed document. Unlike the manual-flag reprocess,
  // this just re-queues a plain detection job — mirroring the original upload's
  // async detection — so the reviewer can recover from a transient failure with
  // one click. Optimistically mark the doc processing so the sidebar swaps its
  // "Processing failed" state for "Analyzing…"; the 3s poll takes over from there.
  const handleRetryProcessing = useCallback(async (documentId: string) => {
    try {
      await redactionApi.createJobAsync({
        document_id: documentId,
        auto_approve: false,
        // OCR is always on; the backend only runs it on pages that need it.
        enable_ocr: true,
        // Match the case wizard's initial detection backend.
        llm_provider: 'sensitive',
      });
      setFailedDocumentIds((prev) => {
        const next = new Set(prev);
        next.delete(documentId);
        return next;
      });
      setProcessingDocumentIds((prev) => new Set([...prev, documentId]));
    } catch (err) {
      console.error('Failed to retry processing:', err);
    }
  }, []);

  const handleUploadDocuments = () => {
    setIsUploadModalOpen(true);
  };

  const handleDocumentsAdded = useCallback(async () => {
    // Refresh the documents list
    if (caseId) {
      try {
        const response = await casesApi.getCase(caseId, { includeProgress: true });
        setCaseData(response);
        setDocuments(response.documents);
      } catch (err) {
        console.error('Failed to refresh documents:', err);
      }
    }
  }, [caseId]);

  // Upload files into the current case via the SAME presigned-with-fallback
  // path the create-case wizard uses, queue async detection for the successful
  // ones, then refresh the tree so the new documents appear. Matches the
  // wizard's job defaults (OCR on, sensitive LLM).
  const uploadDroppedFiles = useCallback(async (files: File[]) => {
    if (!caseId || files.length === 0) return;

    const uploaded = await Promise.all(
      files.map((file) =>
        uploadDocumentWithFallback(file, { caseId }).catch((err) => {
          console.error(`Failed to upload ${file.name}:`, err);
          return null;
        })
      )
    );

    const successfulDocs = uploaded.filter(
      (doc): doc is DocumentUploadResponse => doc !== null
    );

    await Promise.all(
      successfulDocs.map((doc) =>
        redactionApi.createJobAsync({
          document_id: doc.id,
          auto_approve: false,
          // OCR is always on; the backend only runs it on pages that need it.
          enable_ocr: true,
          // Default to the on-prem (sensitive) model, matching the wizard.
          llm_provider: 'sensitive',
        }).catch((err) => {
          console.error(`Failed to queue detection for ${doc.name}:`, err);
        })
      )
    );

    await handleDocumentsAdded();
  }, [caseId, handleDocumentsAdded]);

  // Files dropped onto the documents sidebar. Files whose names already exist
  // among the case's documents (or repeat within the drop) wait for the user to
  // confirm re-upload via DuplicateUploadDialog; the rest upload immediately.
  const handleFilesDropped = useCallback((files: File[]) => {
    if (!caseId || files.length === 0) return;

    const seenNames = new Set(documents.map((doc) => doc.name));
    const fresh: File[] = [];
    const duplicates: File[] = [];
    for (const file of files) {
      (seenNames.has(file.name) ? duplicates : fresh).push(file);
      seenNames.add(file.name);
    }
    if (duplicates.length > 0) setPendingDuplicateDrop((prev) => [...prev, ...duplicates]);
    if (fresh.length > 0) void uploadDroppedFiles(fresh);
  }, [caseId, documents, uploadDroppedFiles]);

  const handleDocumentStatusChange = async (status: DocumentStatus) => {
    if (!selectedDocumentId || !caseId) return;

    try {
      await casesApi.updateDocumentStatus(caseId, selectedDocumentId, status);

      // Update local state
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === selectedDocumentId ? { ...doc, status } : doc
        )
      );

      // The viewer shows the redacted file only for completed documents, so a
      // status change across that boundary needs an explicit content reload
      // (document loads no longer re-run on documents-list refreshes).
      const versionChanged =
        (currentDocument?.status === 'completed') !== (status === 'completed');
      if (currentDocument && versionChanged) {
        loadDocumentContent({ ...currentDocument, status });
      }
    } catch (err) {
      console.error('Failed to update document status:', err);
    }
  };

  const handleDownloadDocument = useCallback(async (documentId: string, version: 'original' | 'redacted') => {
    const key = `${documentId}:${version}`;
    setDownloadingDocIds((prev) => new Set(prev).add(key));
    try {
      await saveDocumentToDisk(documentId, version);
    } catch (err) {
      console.error('Download failed', err);
    } finally {
      setDownloadingDocIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, []);

  const handleDownload = async (version: 'original' | 'redacted', format?: 'pdf') => {
    if (!selectedDocumentId || !currentDocument) return;

    setDownloadingVersion(format ? `${version}-${format}` : version);
    try {
      // Provide fallback filename based on version and document name
      const fallbackFilename = version === 'redacted'
        ? `redacted_${currentDocument.name}`
        : currentDocument.name;
      await saveDocumentToDisk(selectedDocumentId, version, { fallbackFilename, format });
    } catch (err) {
      console.error('Failed to download document:', err);
    } finally {
      setDownloadingVersion(null);
    }
  };

  const handleDownloadCase = async () => {
    if (!caseId) return;
    setIsDownloadingZip(true);
    try {
      triggerBrowserSave(await casesApi.downloadCaseAsZip(caseId, 'redacted'));
    } catch (err) {
      console.error('Failed to download case as ZIP:', err);
    } finally {
      setIsDownloadingZip(false);
    }
  };

  const handleExportMegaPdf = async () => {
    if (!caseId) return;
    setIsExportingPdf(true);
    try {
      triggerBrowserSave(await casesApi.exportCaseAsMegaPdf(caseId));
    } catch (err) {
      console.error('Failed to export case as mega-PDF:', err);
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleApprove = useCallback(async (entityIds: string[]) => {
    if (!currentJobId) return;

    // Build entity_reasons map for approved entities
    const entityReasonsObj: Record<string, string> = {};
    entityIds.forEach((id) => {
      const reason = entityReasons.get(id);
      if (reason) {
        entityReasonsObj[id] = reason;
      }
    });

    // Optimistic update
    const prevSuggestions = suggestionsRef.current;
    const idSet = new Set(entityIds);
    setSuggestions((prev) =>
      prev.map((s) => {
        if (!idSet.has(s.entityId)) return s;
        const reason = entityReasons.get(s.entityId);
        return {
          ...s,
          status: 'approved' as const,
          replacementText: reason ? `[${reason}]` : s.replacementText,
        };
      })
    );

    markPending(entityIds, 'approved');
    try {
      await redactionApi.approveSuggestions(currentJobId, {
        approved_entities: entityIds,
        entity_reasons: entityReasonsObj,
        entity_versions: buildEntityVersions(prevSuggestions, entityIds),
        apply_redactions: false,
      });
      loadEntityProgress();
    } catch (err) {
      console.error('Failed to approve suggestions:', err);
      if (isVersionConflict(err)) {
        refreshAfterConflict(entityIds);
        return;
      }
      clearPending(entityIds);
      setSuggestions(prevSuggestions);
    }
  }, [currentJobId, entityReasons, markPending, clearPending, loadEntityProgress, setSuggestions, refreshAfterConflict]);

  const handleReject = useCallback(async (entityIds: string[]) => {
    if (!currentJobId) return;

    // Optimistic update
    const prevSuggestions = suggestionsRef.current;
    const idSet = new Set(entityIds);
    setSuggestions((prev) =>
      prev.map((s) =>
        idSet.has(s.entityId) ? { ...s, status: 'rejected' as const } : s
      )
    );

    markPending(entityIds, 'rejected');
    try {
      await redactionApi.approveSuggestions(currentJobId, {
        approved_entities: [],
        rejected_entities: entityIds,
        entity_reasons: {},
        entity_versions: buildEntityVersions(prevSuggestions, entityIds),
        apply_redactions: false,
      });
      loadEntityProgress();
    } catch (err) {
      console.error('Failed to reject suggestions:', err);
      if (isVersionConflict(err)) {
        refreshAfterConflict(entityIds);
        return;
      }
      clearPending(entityIds);
      setSuggestions(prevSuggestions);
    }
  }, [currentJobId, markPending, clearPending, loadEntityProgress, setSuggestions, refreshAfterConflict]);

  const handleReasonChange = useCallback((entityId: string, reason: string) => {
    setEntityReasons((prev) => {
      const newMap = new Map(prev);
      if (reason) {
        newMap.set(entityId, reason);
      } else {
        newMap.delete(entityId);
      }
      return newMap;
    });
  }, []);

  // Approve a single suggestion with an inline reason (called from PDF hover tooltip)
  const handleApproveWithReason = useCallback(async (entityId: string, reason?: string) => {
    if (!currentJobId) return;
    if (reason) {
      handleReasonChange(entityId, reason);
    }
    // Optimistic update
    const prevSuggestions = suggestionsRef.current;
    setSuggestions(prev =>
      prev.map(s =>
        s.entityId === entityId
          ? { ...s, status: 'approved' as const, replacementText: reason ? `[${reason}]` : s.replacementText }
          : s
      )
    );
    markPending([entityId], 'approved');
    try {
      const entityReasonsObj: Record<string, string> = {};
      if (reason) entityReasonsObj[entityId] = reason;
      await redactionApi.approveSuggestions(currentJobId, {
        approved_entities: [entityId],
        entity_reasons: entityReasonsObj,
        entity_versions: buildEntityVersions(prevSuggestions, [entityId]),
        apply_redactions: false,
      });
    } catch (err) {
      console.error('Failed to approve suggestion:', err);
      if (isVersionConflict(err)) {
        refreshAfterConflict([entityId]);
        return;
      }
      clearPending([entityId]);
      setSuggestions(prevSuggestions);
    }
  }, [currentJobId, handleReasonChange, markPending, clearPending, setSuggestions, refreshAfterConflict]);

  // Reject a single suggestion (called from PDF hover tooltip dismiss)
  const handleRejectSingle = (entityId: string) => handleReject([entityId]);

  // Delete a manual entity completely (used for blackout page toggle-off)
  const handleDeleteManualEntity = useCallback(async (entityId: string) => {
    if (!currentJobId) return;
    // Optimistic update
    const prevSuggestions = suggestionsRef.current;
    const expectedVersion = prevSuggestions.find((s) => s.entityId === entityId)?.version;
    setSuggestions(prev => prev.filter(s => s.entityId !== entityId));
    try {
      await redactionApi.deleteManualEntity(currentJobId, entityId, expectedVersion);
    } catch (err) {
      console.error('Failed to delete manual entity:', err);
      if (isVersionConflict(err)) {
        refreshAfterConflict([entityId]);
        return;
      }
      setSuggestions(prevSuggestions);
    }
  }, [currentJobId, setSuggestions, refreshAfterConflict]);

  // Restore a rejected entity to pending (called from PDF hover tooltip on rejected entities)
  const handleRestoreToPending = useCallback(async (entityId: string) => {
    if (!currentJobId) return;
    // Optimistic update
    const prevSuggestions = suggestionsRef.current;
    setSuggestions(prev =>
      prev.map(s =>
        s.entityId === entityId
          ? { ...s, status: 'pending' as const, replacementText: undefined }
          : s
      )
    );
    markPending([entityId], 'pending');
    try {
      await redactionApi.approveSuggestions(currentJobId, {
        approved_entities: [],
        pending_entities: [entityId],
        entity_reasons: {},
        entity_versions: buildEntityVersions(prevSuggestions, [entityId]),
        apply_redactions: false,
      });
    } catch (err) {
      console.error('Failed to restore suggestion to pending:', err);
      if (isVersionConflict(err)) {
        refreshAfterConflict([entityId]);
        return;
      }
      clearPending([entityId]);
      setSuggestions(prevSuggestions);
    }
  }, [currentJobId, markPending, clearPending, setSuggestions, refreshAfterConflict]);

  // Change the entity type of an existing entity from the redact box.
  const handleChangeEntityType = useCallback(async (entityId: string, newType: EntityType) => {
    if (!currentJobId) return;
    // Optimistic update
    const prevSuggestions = suggestionsRef.current;
    const expectedVersion = prevSuggestions.find((s) => s.entityId === entityId)?.version;
    setSuggestions(prev =>
      prev.map(s => (s.entityId === entityId ? { ...s, entityType: newType } : s))
    );
    try {
      await redactionApi.updateEntityType(currentJobId, entityId, newType, expectedVersion);
    } catch (err) {
      console.error('Failed to change entity type:', err);
      if (isVersionConflict(err)) {
        refreshAfterConflict([entityId]);
        return;
      }
      setSuggestions(prevSuggestions);
    }
  }, [currentJobId, setSuggestions, refreshAfterConflict]);

  // Persist a File/Case/Global scope decision as a dictionary rule. Routing scope
  // decisions through rules — rather than only mutating the current job's entities —
  // makes them durable (they apply to future pages and documents) and safe during an
  // in-progress scan: a rule write lands in a separate collection and bumps the job's
  // rules_version, so it never races the pipeline's entity writes. Already-scanned
  // occurrences are reconciled (rescan when post-scan, the pipeline's final pass while
  // still scanning). See docs/live-page-preview-design.md §6.
  const createScopeRule = useCallback(async (
    word: string,
    scope: 'file' | 'case' | 'global',
    action: 'always_redact' | 'always_allow',
    reason?: string,
  ) => {
    const scopeId = scope === 'case' ? caseId : scope === 'file' ? (selectedDocumentId ?? undefined) : undefined;
    // File/case rules need a concrete target — skip rather than create a tenant-wide rule.
    if (scope !== 'global' && !scopeId) return undefined;
    try {
      return await dictionaryApi.createEntry({
        scope_type: scope,
        scope_id: scopeId,
        word,
        action,
        match_type: 'exact',
        reason: reason && reason !== 'N/A' ? reason : undefined,
      });
    } catch (err) {
      console.warn('Failed to persist scope decision as a dictionary rule:', err);
      return undefined;
    }
  }, [caseId, selectedDocumentId]);

  // Approve all matching entities with the same text (scope-aware reason application)
  const handleApproveWithScope = useCallback(async (entityId: string, reason: string | undefined, scope: 'file' | 'case' | 'global') => {
    if (!currentJobId) return;

    // Find the clicked suggestion to get its text
    const currentSuggestions = suggestionsRef.current;
    const clickedSuggestion = currentSuggestions.find(s => s.entityId === entityId);
    if (!clickedSuggestion) return;

    // Find ALL suggestions with the same text — case-insensitively, matching the
    // dictionary rule this decision creates (rules match case-insensitively, and
    // the group panel now fires ONE call per case-equivalent variant class).
    const clickedText = clickedSuggestion.text.toLowerCase();
    const matchingIds = currentSuggestions
      .filter(s => s.text.toLowerCase() === clickedText && s.status !== 'approved')
      .map(s => s.entityId);

    // Build reasons map for all matching entities
    const entityReasonsObj: Record<string, string> = {};
    if (reason) {
      matchingIds.forEach(id => {
        entityReasonsObj[id] = reason;
        handleReasonChange(id, reason);
      });
    }

    // Optimistic update
    const prevSuggestions = currentSuggestions;
    const idSet = new Set(matchingIds);
    setSuggestions(prev =>
      prev.map(s =>
        idSet.has(s.entityId)
          ? { ...s, status: 'approved' as const, replacementText: reason ? `[${reason}]` : s.replacementText }
          : s
      )
    );

    // Optimistic policy: a rule flips only the clicked + already-loaded matches
    // now; the rescan brings in occurrences on other pages. Mark them pending so
    // the rescan reload can't revert them, and spin the sidebar rescan dot.
    markPending(matchingIds, 'approved');
    seedRescanDot(selectedDocumentId);
    try {
      await redactionApi.approveSuggestions(currentJobId, {
        approved_entities: matchingIds,
        entity_reasons: entityReasonsObj,
        entity_versions: buildEntityVersions(prevSuggestions, matchingIds),
        apply_redactions: false,
      });
      // Make the decision durable: "Redact for File/Case/Global" is a rule, not just
      // a one-time flip of the current occurrences (§6). The approve above gives
      // instant, persistent feedback on already-detected occurrences; the rule covers
      // future pages/documents and is reconciled by the pipeline's final pass if the
      // job is still scanning.
      await createScopeRule(clickedSuggestion.text, scope, 'always_redact', reason);
      // The rescan runs on the worker for every scope — just start the poll and let
      // it drive everything. loadEntityProgress picks up the `rescanning` job (file
      // scope is claimed synchronously; case/global enqueue in a background task after
      // the response, so probe a few times), which spins the sidebar dot and, once the
      // rescan finishes, reloads the open file's suggestions via the completion effect.
      // Don't ALSO refetch suggestions here: that immediate fetch lands a premature,
      // pre-rescan merge, so the panel visibly updated twice (once now, then again with
      // the tick on completion). loadedJobStatus === 'rescanning' has no live-preview
      // effect, so there is nothing gained by flipping it early.
      watchForRescans(scope);
    } catch (err) {
      console.error('Failed to approve suggestions with scope:', err);
      // The rule was not created either way, so the rescan dot must resolve.
      failRescanDot(selectedDocumentId);
      if (isVersionConflict(err)) {
        refreshAfterConflict(matchingIds);
        return;
      }
      clearPending(matchingIds);
      setSuggestions(prevSuggestions);
    }
  }, [currentJobId, handleReasonChange, watchForRescans, selectedDocumentId, createScopeRule, markPending, clearPending, setSuggestions, seedRescanDot, failRescanDot, refreshAfterConflict]);

  // Reject all matching entities with the same text (scope-aware) — mirrors
  // handleApproveWithScope so "Allow for File/Case" flips every occurrence at
  // once, not just the clicked one.
  const handleRejectWithScope = useCallback(async (entityId: string, scope: 'file' | 'case' | 'global') => {
    if (!currentJobId) return;

    const currentSuggestions = suggestionsRef.current;
    const clickedSuggestion = currentSuggestions.find(s => s.entityId === entityId);
    if (!clickedSuggestion) return;

    // Find ALL suggestions with the same text (case-insensitive, mirroring the
    // always_allow rule's matching) that aren't already rejected.
    const clickedText = clickedSuggestion.text.toLowerCase();
    const matchingIds = currentSuggestions
      .filter(s => s.text.toLowerCase() === clickedText && s.status !== 'rejected')
      .map(s => s.entityId);

    // Optimistic update
    const prevSuggestions = currentSuggestions;
    const idSet = new Set(matchingIds);
    setSuggestions(prev =>
      prev.map(s =>
        idSet.has(s.entityId) ? { ...s, status: 'rejected' as const } : s
      )
    );

    markPending(matchingIds, 'rejected');
    seedRescanDot(selectedDocumentId);
    try {
      await redactionApi.approveSuggestions(currentJobId, {
        approved_entities: [],
        rejected_entities: matchingIds,
        entity_reasons: {},
        entity_versions: buildEntityVersions(prevSuggestions, matchingIds),
        apply_redactions: false,
      });
      // Durable "Allow for File/Case/Global" → always_allow rule (mirror of the
      // approve path, §6). The reject above flips current occurrences immediately;
      // the rule keeps the term allowed on future pages/documents.
      await createScopeRule(clickedSuggestion.text, scope, 'always_allow');
      // Worker-driven rescan for every scope (mirror of the approve path). Just start
      // the poll: loadEntityProgress picks up the `rescanning` job and drives both the
      // sidebar dot and the completion-effect suggestions reload. No immediate refetch
      // here — it only added a premature, pre-rescan update before the tick.
      watchForRescans(scope);
    } catch (err) {
      console.error('Failed to reject suggestions with scope:', err);
      failRescanDot(selectedDocumentId);
      if (isVersionConflict(err)) {
        refreshAfterConflict(matchingIds);
        return;
      }
      clearPending(matchingIds);
      setSuggestions(prevSuggestions);
    }
  }, [currentJobId, watchForRescans, selectedDocumentId, createScopeRule, markPending, clearPending, setSuggestions, seedRescanDot, failRescanDot, refreshAfterConflict]);

  // Scoped decision about whole entity GROUPS, addressed by group identity. ONE
  // request: the server resolves membership at write time (so occurrences a
  // rescan added after our last read are covered too), flips the rows, upserts
  // one rule per distinct member text under a single rules_version bump, and
  // enqueues one multi-word targeted rescan. This replaces the per-variant
  // burst of approve + dictionary-create pairs, whose N distinct rule creates
  // degraded every multi-variant group action into a full-document rescan.
  const handleGroupDecisionWithScope = useCallback(async (
    action: 'approve' | 'reject',
    entityIds: string[],
    groupKeys: string[],
    scope: 'file' | 'case' | 'global',
    reason?: string,
  ) => {
    if (!currentJobId || groupKeys.length === 0) return;

    const currentSuggestions = suggestionsRef.current;
    const prevSuggestions = currentSuggestions;
    const newStatus = action === 'approve' ? 'approved' as const : 'rejected' as const;
    // Optimistic policy mirrors the server's write: flip every non-overridden
    // member not already at the target status; the rescan brings in occurrences
    // on other pages/documents. Mark them pending so a stale reload can't
    // revert them, and spin the sidebar rescan dot.
    const idSet = new Set(entityIds);
    const affectedIds = currentSuggestions
      .filter(s => idSet.has(s.entityId) && s.status !== newStatus && !s.manuallyOverridden)
      .map(s => s.entityId);
    if (action === 'approve' && reason) {
      affectedIds.forEach(id => handleReasonChange(id, reason));
    }
    const affectedSet = new Set(affectedIds);
    setSuggestions(prev =>
      prev.map(s =>
        affectedSet.has(s.entityId)
          ? {
              ...s,
              status: newStatus,
              replacementText: action === 'approve' && reason ? `[${reason}]` : s.replacementText,
            }
          : s
      )
    );
    markPending(affectedIds, newStatus);
    seedRescanDot(selectedDocumentId);
    try {
      const decision = { group_keys: groupKeys, scope, reason: action === 'approve' ? reason : undefined };
      const response = action === 'approve'
        ? await redactionApi.approveGroups(currentJobId, decision)
        : await redactionApi.rejectGroups(currentJobId, decision);
      if (response.rescan === 'none') {
        // No rule changed (idempotent re-click) — nothing will rescan, so
        // resolve the dot to a tick now instead of spinning until the fallback
        // timer. The row flips (if any) were still applied.
        if (selectedDocumentId) {
          clearRescanDotTimer(selectedDocumentId);
          setRescanDotPhase(selectedDocumentId, 'check');
          scheduleRescanDotClear(selectedDocumentId);
        }
        clearPending(affectedIds);
        loadEntityProgress();
      } else {
        watchForRescans(scope);
      }
    } catch (err) {
      console.error(`Failed to ${action} groups with scope:`, err);
      failRescanDot(selectedDocumentId);
      clearPending(affectedIds);
      setSuggestions(prevSuggestions);
    }
  }, [currentJobId, handleReasonChange, watchForRescans, loadEntityProgress, selectedDocumentId, markPending, clearPending, setSuggestions, seedRescanDot, failRescanDot, clearRescanDotTimer, setRescanDotPhase, scheduleRescanDotClear]);

  const handleGroupApproveWithScope = useCallback((
    entityIds: string[], groupKeys: string[], scope: 'file' | 'case' | 'global', reason?: string,
  ) => handleGroupDecisionWithScope('approve', entityIds, groupKeys, scope, reason),
  [handleGroupDecisionWithScope]);

  const handleGroupRejectWithScope = useCallback((
    entityIds: string[], groupKeys: string[], scope: 'file' | 'case' | 'global',
  ) => handleGroupDecisionWithScope('reject', entityIds, groupKeys, scope),
  [handleGroupDecisionWithScope]);

  // De-redact an approved suggestion (revert to pending), with optional scope
  const handleDeRedact = useCallback(async (entityId: string, scope: 'once' | 'file' | 'case' = 'once', word?: string) => {
    if (!currentJobId) return;

    // Compute matching ids for scope-based de-redact
    const currentSuggestions = suggestionsRef.current;
    const matchingIds = (scope === 'file' || scope === 'case') && word
      ? currentSuggestions
          .filter(s => s.text.toLowerCase() === word.toLowerCase() && s.status === 'approved')
          .map(s => s.entityId)
      : [entityId];

    // Optimistic update
    const prevSuggestions = suggestionsRef.current;
    const idSet = new Set(matchingIds);
    setSuggestions(prev =>
      prev.map(s =>
        idSet.has(s.entityId)
          ? { ...s, status: 'pending' as const, replacementText: undefined }
          : s
      )
    );

    markPending(matchingIds, 'pending');
    if (scope === 'file' || scope === 'case') seedRescanDot(selectedDocumentId);
    try {
      if (matchingIds.length > 0) {
        await redactionApi.approveSuggestions(currentJobId, {
          approved_entities: [],
          pending_entities: matchingIds,
          entity_reasons: {},
          entity_versions: buildEntityVersions(prevSuggestions, matchingIds),
          apply_redactions: false,
        });
      }

      // Dictionary cleanup (stays after API success — irreversible side effect)
      if (scope === 'file' && word) {
        try {
          if (selectedDocumentId) {
            const fileEntries = await dictionaryApi.getForFile(selectedDocumentId);
            const toDelete = fileEntries.entries.filter(
              e => e.word.toLowerCase() === word.toLowerCase() && e.action === 'always_redact'
            );
            await Promise.all(toDelete.map(entry => dictionaryApi.deleteEntry(entry.id)));
          }
        } catch (dictErr) {
          console.warn('Failed to clean up file dictionary entries:', dictErr);
        }
        // Mirror the "Redact for File" path: deleting the rule triggers a
        // worker-driven file rescan. Just start the poll — it registers the in-flight
        // job, spins the dot, and reloads the open file's suggestions on completion.
        // No immediate refetch: it only added a premature update before the tick.
        watchForRescans('file');
      } else if (scope === 'case' && word && selectedDocumentId && caseId) {
        try {
          const caseEntries = await dictionaryApi.getForCase(caseId);
          const toDelete = caseEntries.entries.filter(
            e => e.word.toLowerCase() === word.toLowerCase() && e.action === 'always_redact'
          );
          await Promise.all(toDelete.map(entry => dictionaryApi.deleteEntry(entry.id)));
        } catch (dictErr) {
          console.warn('Failed to clean up case dictionary entries:', dictErr);
        }
        // Case-scope rule deletion enqueues its rescan in a background task after the
        // response, so a single read can race ahead of the flip to `rescanning`. Probe
        // a few times so the poll reliably starts and the dot appears on every doc.
        watchForRescans('case');
      }
    } catch (err) {
      console.error('Failed to de-redact suggestion:', err);
      if (scope === 'file' || scope === 'case') failRescanDot(selectedDocumentId);
      if (isVersionConflict(err)) {
        refreshAfterConflict(matchingIds);
        return;
      }
      clearPending(matchingIds);
      setSuggestions(prevSuggestions);
    }
  }, [currentJobId, selectedDocumentId, caseId, watchForRescans, markPending, clearPending, setSuggestions, seedRescanDot, failRescanDot, refreshAfterConflict]);

  // Manual selection mode state for text-based documents
  const [isManualSelectionMode, setIsManualSelectionMode] = useState(false);
  // Active PDF drawing tool, lifted here so the selection persists across documents.
  const [activeTool, setActiveTool] = useState<RedactionTool>('blackout');

  // Handle manual redaction for PDF (bounding box or text selection)
  // Pending manual redaction awaiting type/reason selection in the modal.
  // `kind` distinguishes the PDF (bounds) path from the text-based path so we
  // can run the right persistence logic on confirm.
  const [pendingManualRedaction, setPendingManualRedaction] = useState<
    | { documentId: string; kind: 'pdf'; bounds: { x: number; y: number; width: number; height: number }; page: number; text?: string; position?: { x: number; y: number } }
    | { documentId: string; kind: 'text'; text: string; position?: { x: number; y: number } }
    | null
  >(null);
  const visiblePendingManualRedaction = pendingManualRedaction?.documentId === selectedDocumentId
    ? pendingManualRedaction
    : null;

  // Stable props for the memoized PDFEditor/PDFViewer: a fresh object/array each
  // render would defeat React.memo on every poll tick.
  const pdfPendingManualBox = useMemo(
    () => (visiblePendingManualRedaction?.kind === 'pdf'
      ? { bounds: visiblePendingManualRedaction.bounds, page: visiblePendingManualRedaction.page }
      : null),
    [visiblePendingManualRedaction],
  );
  const pdfReadyPages = useMemo(
    () => (isLivePreview && !isScanFinalizing ? contiguousReadyPages ?? EMPTY_READY_PAGES : undefined),
    [isLivePreview, isScanFinalizing, contiguousReadyPages],
  );

  // Holds the in-flight "what text is inside the drawn box?" request so the
  // popover interaction and the lookup run in parallel. Resolved when the user
  // confirms the manual redaction.
  const pendingRegionTextRef = useRef<Promise<TextInRegionResponse> | null>(null);

  // Values the entity picker collects and hands to the persistence helpers.
  type EntitySelection = { entity_type: EntityType; reason?: string };

  // Persist a manual PDF (bounding-box) redaction with the chosen type + reason.
  const persistManualPdfRedaction = useCallback(async (
    data: { bounds: { x: number; y: number; width: number; height: number }; page: number; text?: string },
    selection: EntitySelection,
  ) => {
    if (!currentJobId) {
      console.warn('No active redaction job - please run PII detection first');
      return;
    }

    const inlineText = data.text?.trim() || '';
    // A background region-text lookup was kicked off when the box was drawn.
    const regionPromise = pendingRegionTextRef.current;
    pendingRegionTextRef.current = null;
    const awaitingRegionText = !inlineText && !!regionPromise;

    // Optimistic: show the highlight immediately with a temporary index. If we're
    // still resolving the region text, mark it pending so the panel shows a spinner.
    const tempId = `temp-${Date.now()}`;
    const optimisticSuggestion = {
      entityId: tempId,
      index: -1,
      entityType: selection.entity_type,
      text: inlineText || '[Manual Redaction]',
      confidence: 1.0,
      page: data.page,
      bounds: data.bounds,
      status: 'pending' as const,
      source: 'manual' as const,
      isOptimistic: true,
      textPending: awaitingRegionText,
    };
    setSuggestions(prev => [...prev, optimisticSuggestion]);

    // Resolve the region-text lookup before persisting, so the manual entity is
    // stored WITH its text and reprocessing can steer on it.
    let entityText = inlineText;
    if (awaitingRegionText && regionPromise) {
      try {
        const region = await regionPromise;
        entityText = region.text?.trim() || '';
      } catch (err) {
        console.error('Failed to extract text in region:', err);
        entityText = '';
      }
    }

    try {
      const response = await redactionApi.addManualEntity(currentJobId, {
        text: entityText,
        page: data.page,
        bounds: data.bounds,
        entity_type: selection.entity_type,
        reason: selection.reason && selection.reason !== 'N/A' ? selection.reason : undefined,
      });
      if (suggestionsDocIdRef.current !== selectedDocumentId) return;

      // Silently replace optimistic entry with real one from backend
      setSuggestions(prev => prev.map(s =>
        s.entityId === tempId
          ? { ...s, entityId: response.entity_id, entityType: (response.entity_type as EntityType) || selection.entity_type, text: response.text || entityText || '[Manual Redaction]', isOptimistic: undefined, textPending: false }
          : s
      ));
      setSelectedSuggestionIndex(prev => prev === tempId ? response.entity_id : prev);

      // Mirror the reason into local state so the UI reflects it without a refetch.
      if (selection.reason && selection.reason !== 'N/A') {
        handleReasonChange(response.entity_id, selection.reason);
      }
    } catch (err) {
      console.error('Failed to add manual redaction:', err);
      // Remove the optimistic entry on failure
      setSuggestions(prev => prev.filter(s => s.entityId !== tempId));
    }
  }, [currentJobId, handleReasonChange, selectedDocumentId, setSuggestions]);

  // Triggered by the PDF viewer: open the entity-selection modal instead of
  // creating the entity immediately.
  const handleManualPdfRedaction = useCallback((data: { bounds: { x: number; y: number; width: number; height: number }; page: number; reason?: string; text?: string; position?: { x: number; y: number } }) => {
    if (!currentJobId || !selectedDocumentId) {
      console.warn('No active redaction job - please run PII detection first');
      return;
    }
    // Area draws arrive without text; fetch what the document already has inside
    // the box in the background so it's ready by the time the user confirms.
    pendingRegionTextRef.current = data.text?.trim()
      ? null
      : redactionApi.getTextInRegion(currentJobId, { page: data.page, bounds: data.bounds });
    setPendingManualRedaction({
      documentId: selectedDocumentId,
      kind: 'pdf',
      bounds: data.bounds,
      page: data.page,
      text: data.text,
      position: data.position,
    });
  }, [currentJobId, selectedDocumentId]);

  // Handle page blackout toggle via dedicated endpoint
  const handleBlackoutPage = useCallback(async (page: number, reason?: string) => {
    if (!currentJobId) {
      console.warn('No active redaction job - please run PII detection first');
      return;
    }

    // Determine if this is a create or remove toggle
    const existing = suggestionsRef.current.find(s => s.text === '[Page Blackout]' && s.page === page && s.source === 'manual' && s.status !== 'rejected');
    const isRemoving = !!existing;

    // Optimistic update
    const tempId = `temp-${Date.now()}`;
    if (isRemoving) {
      setSuggestions(prev => prev.filter(s => s !== existing));
    } else {
      setSuggestions(prev => [...prev, {
        entityId: tempId,
        index: -1,
        entityType: 'CUSTOM' as EntityType,
        text: '[Page Blackout]',
        confidence: 1.0,
        page,
        bounds: { x: 0, y: 0, width: 0, height: 0 },
        status: 'approved',
        source: 'manual',
        replacementText: reason ? `[${reason}]` : undefined,
        isOptimistic: true,
      }]);
    }

    try {
      const response = await redactionApi.blackoutPage(currentJobId, { page, reason });
      if (suggestionsDocIdRef.current !== selectedDocumentId) return;

      if (response.action === 'created') {
        // Replace optimistic entry with real one
        setSuggestions(prev => prev.map(s =>
          s.entityId === tempId
            ? { ...s, entityId: response.entity_id, page: response.page, isOptimistic: undefined }
            : s
        ));
        setSelectedSuggestionIndex(prev => prev === tempId ? response.entity_id : prev);
      }
      // For 'removed', optimistic delete already handled it
    } catch (err) {
      console.error('Failed to toggle page blackout:', err);
      // Rollback: restore removed entry or remove optimistic add
      if (isRemoving && existing) {
        setSuggestions(prev => [...prev, existing]);
      } else {
        setSuggestions(prev => prev.filter(s => s.entityId !== tempId));
      }
    }
  }, [currentJobId, selectedDocumentId, setSuggestions]);

  // Persist a manual text-based (DOCX/EML/MSG) redaction with chosen type + reason.
  const persistManualTextRedaction = useCallback(async (text: string, selection: EntitySelection) => {
    if (!currentJobId || !text.trim()) {
      setIsManualSelectionMode(false);
      return;
    }

    // Optimistic: show the highlight immediately with a temporary id
    const tempId = `temp-${Date.now()}`;
    setSuggestions(prev => [...prev, {
      entityId: tempId,
      index: -1,
      entityType: selection.entity_type,
      text: text.trim(),
      confidence: 1.0,
      page: 0,
      status: 'pending',
      source: 'manual',
      isOptimistic: true,
    }]);

    try {
      const response = await redactionApi.addManualEntity(currentJobId, {
        text: text.trim(),
        page: 0, // Text-based files are single "page"
        entity_type: selection.entity_type,
        reason: selection.reason && selection.reason !== 'N/A' ? selection.reason : undefined,
      });
      if (suggestionsDocIdRef.current !== selectedDocumentId) return;

      // Silently replace optimistic entry with real one from backend
      setSuggestions(prev => prev.map(s =>
        s.entityId === tempId
          ? { ...s, entityId: response.entity_id, entityType: (response.entity_type as EntityType) || selection.entity_type, isOptimistic: undefined }
          : s
      ));
      setSelectedSuggestionIndex(prev => prev === tempId ? response.entity_id : prev);

      // Mirror the reason into local state so the UI reflects it without a refetch.
      if (selection.reason && selection.reason !== 'N/A') {
        handleReasonChange(response.entity_id, selection.reason);
      }
    } catch (err) {
      console.error('Failed to add manual text redaction:', err);
      // Remove the optimistic entry on failure
      setSuggestions(prev => prev.filter(s => s.entityId !== tempId));
    }
    // Stay in highlight mode so the reviewer can keep selecting text without
    // re-enabling the tool after every redaction (mirrors the PDF path).
  }, [currentJobId, handleReasonChange, selectedDocumentId, setSuggestions]);

  // Triggered by the text-based editors: open the entity-selection modal.
  const handleManualTextRedaction = useCallback((text: string, position?: { x: number; y: number }) => {
    if (!currentJobId || !selectedDocumentId || !text.trim()) {
      if (!currentJobId) {
        console.warn('No active redaction job - please run PII detection first');
      }
      setIsManualSelectionMode(false);
      return;
    }
    setPendingManualRedaction({
      documentId: selectedDocumentId,
      kind: 'text',
      text: text.trim(),
      position,
    });
  }, [currentJobId, selectedDocumentId]);

  // Confirm handler shared by both manual-redaction paths. The popover supplies
  // the (possibly user-edited) text plus the chosen entity type + reason.
  const handleConfirmEntitySelection = useCallback((data: { text: string; entityType: EntityType; reason?: string }) => {
    const pending = pendingManualRedaction;
    setPendingManualRedaction(null);
    if (!pending) return;
    const selection: EntitySelection = { entity_type: data.entityType, reason: data.reason };
    if (pending.kind === 'pdf') {
      void persistManualPdfRedaction({ bounds: pending.bounds, page: pending.page, text: data.text }, selection);
    } else {
      void persistManualTextRedaction(data.text, selection);
    }
  }, [pendingManualRedaction, persistManualPdfRedaction, persistManualTextRedaction]);

  // Cancel handler: discard the pending redaction but keep highlight mode on so
  // the reviewer can immediately reselect without re-enabling the tool.
  const handleCancelEntitySelection = useCallback(() => {
    setPendingManualRedaction(null);
  }, []);

  const handleNavigateToSuggestion = useCallback((suggestion: EntitySuggestion) => {
    setSelectedSuggestionIndex(suggestion.entityId);
  }, []);
  const handleDeselectSuggestion = useCallback(() => setSelectedSuggestionIndex(null), []);

  // PST finalize handler - creates ZIP of redacted children
  const handlePstFinalize = useCallback(async () => {
    if (!selectedDocumentId || !caseId) return;

    setIsFinalizingPst(true);
    try {
      // Call the finalize endpoint which creates a ZIP of redacted children
      const result = await pstApi.finalize(selectedDocumentId);

      if (result.status === 'completed') {
        // Refresh case to get updated document status
        const response = await casesApi.getCase(caseId, { includeProgress: true });
        setCaseData(response);
        setDocuments(response.documents);

        // Download the redacted ZIP file
        await saveDocumentToDisk(selectedDocumentId!, 'redacted', {
          fallbackFilename: `redacted_${currentDocument?.name || 'archive.zip'}`,
        });
      }
    } catch (err) {
      console.error('Failed to finalize PST:', err);
    } finally {
      setIsFinalizingPst(false);
    }
  }, [selectedDocumentId, caseId, currentDocument]);

  // Finalize document: apply all approved redactions and mark as completed
  const handleFinalizeDocument = useCallback(async () => {
    if (!currentJobId || !selectedDocumentId || !caseId) return;

    // Check if there are any approved suggestions
    const approvedCount = visibleSuggestions.filter((s) => s.status === 'approved').length;
    if (approvedCount === 0) {
      console.warn('No approved suggestions to finalize');
      return;
    }

    setIsFinalizing(true);
    setFinalizeError(null);
    try {
      // Step 1: Apply all approved redactions to create the redacted file.
      // Run the burn on the Kafka worker off the request path (B1 of sc-6853):
      // the endpoint returns 'redacting' immediately and we poll job status.
      let result = await redactionApi.applyJobRedactions(currentJobId, true);

      if (result.status === 'redacting') {
        // Poll job status until the worker finishes the burn (completed/failed).
        const POLL_INTERVAL_MS = 2000;
        // The worker always writes a terminal status (completed/failed), and on its
        // own timeout (config.worker_timeout, default 2100s) it marks the job failed.
        // Poll past that ceiling (2100s + buffer) so we observe the worker's terminal
        // state rather than giving up early on a still-running burn (which would prompt
        // a retry that double-enqueues the burn).
        const MAX_POLLS = 1110; // ~37 min, just past the 35 min worker timeout
        let terminal = false;
        for (let i = 0; i < MAX_POLLS && !terminal; i++) {
          await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
          const jobStatus = await redactionApi.getJobStatus(currentJobId);
          if (jobStatus.status === 'completed') {
            result = { ...result, status: 'completed' };
            terminal = true;
          } else if (jobStatus.status === 'failed') {
            throw new Error(jobStatus.error_message || 'Redaction failed on the worker');
          }
        }
        if (!terminal) {
          throw new Error('Redaction is taking longer than expected. Please try again.');
        }
      }

      if (result.status === 'completed' || result.status === 'no_changes') {
        // Step 2: Mark document as finalized (completed)
        await casesApi.finalizeDocument(caseId, selectedDocumentId);

        // Update local document state
        setDocuments((prev) =>
          prev.map((doc) =>
            doc.id === selectedDocumentId ? { ...doc, status: 'completed' as DocumentStatus } : doc
          )
        );
        // Refresh case and documents to get updated status
        const response = await casesApi.getCase(caseId, { includeProgress: true });
        setCaseData(response);
        setDocuments(response.documents);

        // Reload document to show redacted version
        if (currentDocument) {
          loadDocumentContent({ ...currentDocument, status: 'completed' as DocumentStatus });
        }
      }
    } catch (err) {
      console.error('Failed to finalize document:', err);
      const message = err instanceof Error ? err.message : 'Finalization failed';
      setFinalizeError(message);
    } finally {
      setIsFinalizing(false);
    }
  }, [currentJobId, selectedDocumentId, caseId, visibleSuggestions, currentDocument]);

  // Unfinalize document: allow re-editing by changing status back to 'redacted'
  const handleUnfinalize = useCallback(async () => {
    if (!selectedDocumentId || !caseId) return;

    setIsUnfinalizing(true);
    try {
      await casesApi.updateDocumentStatus(caseId, selectedDocumentId, 'redacted');

      // Update local state
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === selectedDocumentId ? { ...doc, status: 'redacted' as DocumentStatus } : doc
        )
      );
      // Refresh case and documents to get updated status
      const response = await casesApi.getCase(caseId, { includeProgress: true });
      setCaseData(response);
      setDocuments(response.documents);

      // Reload the editable original version (document loads no longer re-run
      // on documents-list refreshes).
      if (currentDocument) {
        loadDocumentContent({ ...currentDocument, status: 'redacted' as DocumentStatus });
      }
    } catch (err) {
      console.error('Failed to unfinalize document:', err);
    } finally {
      setIsUnfinalizing(false);
    }
  }, [selectedDocumentId, caseId, currentDocument]);

  // Handle print - fetches the redacted PDF and opens browser print dialog
  const handlePrint = useCallback(async () => {
    if (!selectedDocumentId || !currentDocument) return;

    setIsPrinting(true);
    try {
      const blob = await fetchDocumentBlob(selectedDocumentId, 'redacted', {
        fallbackFilename: `redacted_${currentDocument.name}`,
        format: 'pdf',
      });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url);
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
          printWindow.addEventListener('afterprint', () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          });
        });
      } else {
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Failed to print document:', err);
    } finally {
      setIsPrinting(false);
    }
  }, [selectedDocumentId, currentDocument]);

  const handleAddToDictionary = useCallback(async (data: { word: string; scope: 'file' | 'case' | 'global'; action: 'always_redact' | 'always_allow' }) => {
    seedRescanDot(selectedDocumentId);
    try {
      await dictionaryApi.createEntry({
        scope_type: data.scope,
        scope_id: data.scope === 'case' ? caseId : data.scope === 'file' ? (selectedDocumentId ?? undefined) : undefined,
        word: data.word,
        action: data.action,
        match_type: 'exact',
      });
      // Worker-driven rescan for every scope — just start the poll. It detects when
      // the worker finishes (driving the sidebar dot) and reloads the open file's
      // suggestions on completion via the completion effect. No immediate refetch:
      // it only added a premature, pre-rescan update before the tick.
      watchForRescans(data.scope);
    } catch (err) {
      console.error('Failed to add to dictionary:', err);
      failRescanDot(selectedDocumentId);
    }
  }, [caseId, selectedDocumentId, watchForRescans, seedRescanDot, failRescanDot]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-[#7035C4]" />
          <p className="text-muted-foreground">Loading case...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !caseData) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center">
          <p className="text-destructive mb-4">{error || 'Case not found'}</p>
          <Button variant="outline" onClick={handleBack}>
            Back to Cases
          </Button>
        </div>
      </div>
    );
  }

  const fileType = currentDocument?.name.split('.').pop()?.toLowerCase();
  // The viewer chrome (header with title/icon/actions) is driven entirely by
  // `currentDocument`, which comes from the already-loaded case list — no fetch.
  // So we render the chrome the instant a doc is selected and confine the loading
  // spinner to the content region, which waits on `loadDocumentContent` (the
  // presigned-URL round-trip + PDF.js / HTML fetch). Previously the whole pane
  // was gated behind `isLoadingDocument`, so the user saw a full-pane spinner
  // before the title/wireframe even though the title was free.
  const isSelectedContentReady = contentDocumentId === selectedDocumentId;
  // Only consulted for non-PDF types — PDFs mount their editor shell
  // immediately (toolbar and all) and pass a null source until ready.
  const isSelectedContentLoaded = isSelectedContentReady && (
    ((fileType === 'docx' || fileType === 'doc') && htmlContent) ||
    ((fileType === 'eml' || fileType === 'msg') && htmlContent) ||
    isContainerDocument
  );
  // Guarded PDF source for the always-mounted editor. The `isSelectedContentReady`
  // check matters on a doc-switch click: React re-renders with the new
  // `selectedDocumentId` before the load effect clears `pdfData`, so without it
  // the fresh-keyed PDFEditor would receive the previous document's URL for one
  // commit and fire a stale range request. `handleSelectDocument` nulls
  // `contentDocumentId` synchronously, so this starts as null instead.
  const selectedPdfData = fileType === 'pdf' && isSelectedContentReady ? pdfData : null;
  const selectedOriginalPdfData =
    fileType === 'pdf' && isSelectedContentReady ? (originalPdfData ?? undefined) : undefined;
  // Per-document progress is embedded in the case list (sc-6865), so we know
  // up-front — before the suggestions/job fetch returns — whether the selected
  // document has reviewable results and whether it's still scanning. Driving the
  // finalize button and results panel off this (instead of the async `currentJobId`
  // / `visibleSuggestions`) keeps them stable across a document switch rather than
  // disappearing and flickering back in once the fetch lands.
  const selectedDocProgress = currentDocument?.progress;
  const selectedDocHasResults = !!selectedDocProgress?.has_results;
  // Show the finalize button as soon as we know the doc has finalizable results.
  // Excludes still-scanning docs, which surface the live-preview pill instead and
  // would otherwise flash a finalize button before `isLivePreview` resolves true.
  const canFinalizeSelectedDoc = selectedDocHasResults && !selectedDocProgress?.is_processing;
  const handleSelectDocument = (documentId: string) => {
    if (documentId === selectedDocumentId) return;
    contentLoadIdRef.current += 1;
    setDocumentError(null);
    setContentDocumentId(null);
    setSelectedSuggestionIndex(null);
    setSelectedDocumentId(documentId);
  };

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Top Bar */}
      <CaseTopBar
        caseData={caseData}
        onBack={handleBack}
        onUploadDocuments={handleUploadDocuments}
        onOpenCaseDict={caseId ? () => setIsCaseDictModalOpen(true) : undefined}
        onRenameCase={handleRenameCase}
        onDeleteCase={() => setIsDeleteDialogOpen(true)}
        onDownloadCase={handleDownloadCase}
        isDownloadDisabled={!documents.some(doc => doc.status === 'completed')}
        isDownloading={isDownloadingZip}
        onExportMegaPdf={documents.length > 0 ? handleExportMegaPdf : undefined}
        isExporting={isExportingPdf}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Documents Panel (Left) */}
        <DocumentsPanel
          documents={documents}
          selectedDocumentId={selectedDocumentId}
          onSelectDocument={handleSelectDocument}
          isCollapsed={isDocumentsPanelCollapsed}
          onToggleCollapse={() => setIsDocumentsPanelCollapsed(!isDocumentsPanelCollapsed)}
          onDownload={handleDownloadDocument}
          onOpenFileDict={caseId ? (docId) => setFileDictDocId(docId) : undefined}
          onDeleteDocument={handleDeleteDocument}
          onReprocessDocument={handleRequestReprocess}
          onRetryProcessing={handleRetryProcessing}
          entityProgress={entityProgress}
          processingDocumentIds={processingDocumentIds}
          processingProgress={processingProgress}
          processingEta={processingEta}
          failedDocumentIds={failedDocumentIds}
          failureReasons={failureReasons}
          rescanDotStatus={rescanDotStatus}
          downloadingDocIds={downloadingDocIds}
          onFilesDropped={caseId ? handleFilesDropped : undefined}
        />

        {/* Document Viewer + Results Panel */}
        <div className={cn(
          'flex-1 flex overflow-hidden min-w-0',
          dockPosition === 'bottom' ? 'flex-col' : 'flex-row'
        )}>
          {/* Document Viewer (Center) */}
          <div className="flex-1 overflow-hidden bg-gray-50 min-h-0 min-w-0 flex flex-col">
            {!selectedDocumentId ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <FileX className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 mb-4">No document selected</p>
                  {documents.length === 0 && (
                    <Button
                      onClick={handleUploadDocuments}
                      className="bg-[#7035C4] hover:bg-[#5c2a9e] text-white"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Upload Documents
                    </Button>
                  )}
                </div>
              </div>
            ) : documentError ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <FileX className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">{documentError}</p>
                </div>
              </div>
            ) : currentDocument ? (
              <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                {/* Document Header */}
                <div className="bg-card border-b border-border px-6 py-4 shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded">
                        {getFileIconByName(currentDocument?.name || '')}
                      </div>
                      <div>
                        <h3 className="font-medium">{currentDocument?.name || 'Document'}</h3>
                        <p className="text-sm text-muted-foreground">
                          {fileType === 'pdf' && 'PDF Document'}
                          {(fileType === 'eml' || fileType === 'msg') && 'Email'}
                          {(fileType === 'docx' || fileType === 'doc') && 'Word Document'}
                          {fileType === 'pst' && 'PST Archive'}
                          {fileType === 'zip' && 'ZIP Archive'}
                        </p>
                      </div>
                    </div>

                    {/* Header Actions */}
                    <div className="flex items-center gap-2">
                      {/* Print button - only show when finalized */}
                      {currentDocument?.status === 'completed' && (
                        <button
                          onClick={handlePrint}
                          disabled={isPrinting}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-muted rounded-[var(--radius-button)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                          title="Print (Ctrl+P)"
                        >
                          {isPrinting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Printer className="w-4 h-4" />
                          )}
                          <span className="text-sm hidden sm:inline">Print</span>
                        </button>
                      )}

                      {/* Download button - only show when finalized */}
                      {currentDocument?.status === 'completed' && (
                        <div className="relative group">
                          <button
                            disabled={downloadingVersion !== null}
                            className="flex items-center gap-2 px-3 py-2 hover:bg-muted rounded-[var(--radius-button)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                          >
                            {downloadingVersion !== null ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                            <span className="text-sm hidden sm:inline">Download</span>
                          </button>
                          <div className="absolute right-0 top-full mt-1 bg-white border border-border rounded-[var(--radius)] shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 min-w-[120px]">
                            <button
                              onClick={() => handleDownload('original')}
                              disabled={downloadingVersion !== null}
                              className="flex items-center gap-2 w-full px-4 py-2 text-sm text-left hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {downloadingVersion === 'original' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                              Original
                            </button>
                            <button
                              onClick={() => handleDownload('redacted')}
                              disabled={downloadingVersion !== null}
                              className="flex items-center gap-2 w-full px-4 py-2 text-sm text-left hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {downloadingVersion === 'redacted' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                              Redacted
                            </button>
                            {['docx', 'doc', 'eml', 'msg'].includes(fileType || '') && (
                              <button
                                onClick={() => handleDownload('redacted', 'pdf')}
                                disabled={downloadingVersion !== null}
                                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-left hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {downloadingVersion === 'redacted-pdf' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                                Redacted as PDF
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Edit button - only show when finalized, allows re-editing */}
                      {currentDocument?.status === 'completed' && (
                        <button
                          onClick={handleUnfinalize}
                          disabled={isUnfinalizing}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-muted rounded-[var(--radius-button)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                          title="Edit redactions"
                        >
                          {isUnfinalizing ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Pencil className="w-4 h-4" />
                          )}
                          <span className="text-sm hidden sm:inline">Edit</span>
                        </button>
                      )}

                      {/* Live preview badge. Ready pages are editable while the rest
                          of the document scans (Phase 5) — so the copy invites review
                          rather than gating it. Once every page is scanned the job runs
                          its final grouping/re-sort pass, which re-keys the whole list
                          and briefly re-locks editing; the "finalizing" copy makes that
                          collapse-into-groups read as intentional rather than a flicker. */}
                      {isLivePreview && (
                        <span className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          {isScanFinalizing
                            ? 'Finalizing results… grouping detected entities'
                            : loadedTotalPageCount && contiguousReadyPages
                              ? `Live preview — ${contiguousReadyPages.length}/${loadedTotalPageCount} pages processed · all pages editable`
                              : 'Live preview — still scanning… all pages editable'}
                        </span>
                      )}

                      {/* Document Status Select - show when document is not finalized */}
                      {!isLivePreview && currentDocument?.status !== 'completed' && currentDocument?.status && (
                        <Select
                          value={currentDocument.status}
                          onValueChange={(value: string) => handleDocumentStatusChange(value as DocumentStatus)}
                        >
                          <SelectTrigger className="w-[180px]" size="sm">
                            <SelectValue placeholder="Status" />
                          </SelectTrigger>
                          <SelectContent>
                            {(['unprocessed', 'needs_redaction', 'redacted'] as DocumentStatus[]).map((status) => (
                              <SelectItem key={status} value={status}>
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: DOCUMENT_STATUS_CONFIG[status].color }}
                                  />
                                  {DOCUMENT_STATUS_CONFIG[status].label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}

                      {/* Finalize button - different logic based on document type.
                          Visibility keys off `canFinalizeSelectedDoc` (known from
                          embedded progress) as well as `currentJobId` so it stays
                          put across a document switch instead of flickering in once
                          the job fetch lands. It stays disabled until there's an
                          approved suggestion, so showing it early is harmless. */}
                      {!isLivePreview && currentDocument?.status !== 'completed' && (isContainerDocument || currentJobId || canFinalizeSelectedDoc) && (
                        <button
                          onClick={isContainerDocument ? handlePstFinalize : handleFinalizeDocument}
                          disabled={
                            isFinalizing ||
                            isFinalizingPst ||
                            (isContainerDocument
                              ? !allChildrenCompleted
                              : visibleSuggestions.filter((s) => s.status === 'approved').length === 0 ||
                                (isEmailWithChildren && !allChildrenCompleted))
                          }
                          className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title={
                            isContainerDocument
                              ? (!allChildrenCompleted
                                  ? 'Complete all child documents before finalizing'
                                  : 'Finalize and download redacted archive')
                              : isEmailWithChildren && !allChildrenCompleted
                                ? 'Complete all child attachments before finalizing'
                                : visibleSuggestions.filter((s) => s.status === 'approved').length === 0
                                  ? 'Approve at least one suggestion to finalize'
                                  : 'Apply redactions and finalize'
                          }
                        >
                          {(isFinalizing || isFinalizingPst) ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                          <span>Finalize</span>
                        </button>
                      )}
                    </div>
                    {finalizeError && (
                      <p className="text-sm text-red-600 mt-1">{finalizeError}</p>
                    )}
                  </div>
                </div>

                {/* Editor Content — chrome above is already drawn. PDFs mount
                    their editor shell immediately with a null source, so the
                    toolbar renders in the same paint as the header and the
                    page skeleton inside the editor is the single loading
                    treatment (URL resolution + PDF.js load + first paint).
                    Other types keep the content-fetch gate. */}
                <div className="flex-1 overflow-hidden min-h-0">
                  {fileType === 'pdf' ? (
                    /* Fallback covers the rare cold-cache race where the
                       preloaded editor chunk hasn't landed yet — same page
                       skeleton, just without the toolbar row. */
                    <Suspense fallback={<PdfPageSkeleton />}>
                      <PDFEditor
                        key={selectedDocumentId}
                        pdfData={selectedPdfData}
                        originalPdfData={selectedOriginalPdfData}
                        initialPageCount={currentDocument?.page_count}
                        jobId={currentJobId}
                        suggestions={visibleSuggestions}
                        selectedSuggestionIndex={selectedSuggestionIndex}
                        onSelectSuggestion={setSelectedSuggestionIndex}
                        onManualRedactionAdd={handleManualPdfRedaction}
                        pendingManualBox={pdfPendingManualBox}
                        reasons={reasons}
                        onApproveSuggestion={handleApproveWithReason}
                        onRejectSuggestion={handleRejectSingle}
                        onDeleteManualEntity={handleDeleteManualEntity}
                        onDeRedactSuggestion={handleDeRedact}
                        onAddToDictionary={handleAddToDictionary}
                        onApproveWithScope={handleApproveWithScope}
                        onRejectWithScope={handleRejectWithScope}
                        onRestoreToPending={handleRestoreToPending}
                        onChangeEntityType={handleChangeEntityType}
                        isManualSelectionMode={isManualSelectionMode}
                        onManualSelectionModeChange={setIsManualSelectionMode}
                        activeTool={activeTool}
                        onActiveToolChange={setActiveTool}
                        onBlackoutPage={handleBlackoutPage}
                        readyPages={pdfReadyPages}
                        isReadOnly={currentDocument?.status === 'completed'}
                        isPreview={isScanFinalizing}
                        /* Hold the page skeleton across a finalize/edit
                           transition — from click through the burn + version
                           reload — so the viewer shows skeleton → new version,
                           never the stale document or a blank ghost. */
                        externalLoading={isFinalizing || isUnfinalizing}
                      />
                    </Suspense>
                  ) : !isSelectedContentLoaded ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="flex flex-col items-center gap-4">
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                        <p className="text-muted-foreground">Loading document...</p>
                      </div>
                    </div>
                  ) : (
                  <Suspense fallback={<div className='flex items-center justify-center h-full'><Loader2 className='h-8 w-8 animate-spin text-muted-foreground' /></div>}>
                    {(fileType === 'docx' || fileType === 'doc') && htmlContent ? (
                      <DocxEditorView
                        htmlContent={htmlContent}
                        suggestions={visibleSuggestions}
                        selectedSuggestionIndex={selectedSuggestionIndex}
                        onSelectSuggestion={setSelectedSuggestionIndex}
                        isManualSelectionMode={isManualSelectionMode}
                        onManualSelectionModeChange={setIsManualSelectionMode}
                        onManualRedactionAdd={handleManualTextRedaction}
                        reasons={reasons}
                        onApproveSuggestion={handleApproveWithReason}
                        onRejectSuggestion={handleRejectSingle}
                        onDeRedactSuggestion={handleDeRedact}
                        onAddToDictionary={handleAddToDictionary}
                        onApproveWithScope={handleApproveWithScope}
                        onRejectWithScope={handleRejectWithScope}
                        onRestoreToPending={handleRestoreToPending}
                        onChangeEntityType={handleChangeEntityType}
                        isReadOnly={currentDocument?.status === 'completed' || isScanFinalizing}
                      />
                    ) : (fileType === 'eml' || fileType === 'msg') && htmlContent ? (
                      <EmailEditorView
                        htmlContent={htmlContent}
                        suggestions={visibleSuggestions}
                        selectedSuggestionIndex={selectedSuggestionIndex}
                        onSelectSuggestion={setSelectedSuggestionIndex}
                        isManualSelectionMode={isManualSelectionMode}
                        onManualSelectionModeChange={setIsManualSelectionMode}
                        onManualRedactionAdd={handleManualTextRedaction}
                        reasons={reasons}
                        onApproveSuggestion={handleApproveWithReason}
                        onRejectSuggestion={handleRejectSingle}
                        onDeRedactSuggestion={handleDeRedact}
                        onAddToDictionary={handleAddToDictionary}
                        onApproveWithScope={handleApproveWithScope}
                        onRejectWithScope={handleRejectWithScope}
                        onRestoreToPending={handleRestoreToPending}
                        onChangeEntityType={handleChangeEntityType}
                        isReadOnly={currentDocument?.status === 'completed' || isScanFinalizing}
                      />
                    ) : isContainerDocument ? (
                      <PSTEditorView
                        documentId={selectedDocumentId!}
                        childDocuments={childDocuments}
                        documentName={currentDocument?.name || ''}
                      />
                    ) : null}
                  </Suspense>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  <p className="text-muted-foreground">Loading document...</p>
                </div>
              </div>
            )}
          </div>

          {/* Results Panel (Bottom) - shown whenever the selected document has (or
              is loading) results. Keying off the embedded `selectedDocHasResults`
              keeps the panel mounted across a document switch — it renders empty
              briefly while the new suggestions load instead of unmounting and
              animating back open. Its collapsed/expanded state lives in the dock
              hook, so that is preserved regardless. */}
          <Suspense fallback={null}>
          {(visibleSuggestions.length > 0 || isLoadingSelectedSuggestions || selectedDocHasResults) && (
            <ResultsPanel
              suggestions={visibleSuggestions}
              onApprove={handleApprove}
              onReject={handleReject}
              isCollapsed={isResultsPanelCollapsed}
              onToggleCollapse={toggleResultsPanelCollapse}
              isLoading={isLoadingSelectedSuggestions}
              onNavigate={handleNavigateToSuggestion}
              selectedSuggestionIndex={selectedSuggestionIndex}
              reasons={reasons}
              entityReasons={entityReasons}
              onReasonChange={handleReasonChange}
              onDeselectSuggestion={handleDeselectSuggestion}
              dockPosition={dockPosition}
              onSetDockPosition={setDockPosition}
              panelSize={panelSize}
              isDragging={isResultsPanelDragging}
              handleMouseDown={handleResultsPanelMouseDown}
              panelRef={resultsPanelRef}
              onRestoreToPending={handleRestoreToPending}
              onApproveWithScope={handleApproveWithScope}
              onRejectWithScope={handleRejectWithScope}
              onGroupApproveWithScope={handleGroupApproveWithScope}
              onGroupRejectWithScope={handleGroupRejectWithScope}
              isReadOnly={isScanFinalizing}
            />
          )}
          </Suspense>
        </div>
      </div>

      <Suspense fallback={null}>
        {/* Upload Documents Modal */}
        {caseData && (
          <CaseWizardModal
            open={isUploadModalOpen}
            onClose={() => setIsUploadModalOpen(false)}
            mode="add-documents"
            existingCase={caseData}
            onDocumentsAdded={handleDocumentsAdded}
            existingDocumentNames={documents.map((doc) => doc.name)}
          />
        )}

        {/* Duplicate sidebar-drop confirmation */}
        <DuplicateUploadDialog
          open={pendingDuplicateDrop.length > 0}
          fileNames={pendingDuplicateDrop.map((file) => file.name)}
          onConfirm={() => {
            const files = pendingDuplicateDrop;
            setPendingDuplicateDrop([]);
            void uploadDroppedFiles(files);
          }}
          onCancel={() => setPendingDuplicateDrop([])}
        />

        {/* Case Dictionary Setup Modal */}
        {/* Gate the mount on the open flag (not just caseId) so the modal
            remounts each open and useDictionary refetches — otherwise it
            stays permanently mounted and shows a stale list (file/global
            dicts already remount per open). */}
        {caseId && isCaseDictModalOpen && (
          <CaseDictionarySetupModal
            open={isCaseDictModalOpen}
            onClose={() => setIsCaseDictModalOpen(false)}
            onEntriesChanged={() => {
              // Instant feedback on the open document (it's part of the case rescan);
              // other affected docs get their dot as the poll catches each job
              // flipping to `rescanning`. The server enqueues the case rescan as a
              // background task, so poll a few times to bridge that gap.
              seedRescanDot(selectedDocumentId);
              watchForRescans();
            }}
            scopeType="case"
            scopeId={caseId}
            scopeName={caseData?.name}
          />
        )}

        {/* File Dictionary Modal */}
        {fileDictDocId && (
          <CaseDictionarySetupModal
            open={!!fileDictDocId}
            onClose={() => setFileDictDocId(null)}
            onEntriesChanged={() => {
              const docId = fileDictDocId;
              // File-scope rules flip the job to `rescanning` synchronously before
              // the response, so spin the rescan dot for this doc right away, then just
              // start the poll (mirror of the case modal above). The poll picks up the
              // `rescanning` job and reloads the open file's suggestions on completion;
              // an immediate refetch here only added a premature update before the tick.
              if (docId) seedRescanDot(docId);
              watchForRescans();
            }}
            scopeType="file"
            scopeId={fileDictDocId}
            scopeName={documents.find((d) => d.id === fileDictDocId)?.name}
          />
        )}
      </Suspense>

      {/* Delete Case Dialog */}
      <DeleteCaseDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        caseName={caseData?.name ?? ''}
        documentCount={caseData?.document_count ?? 0}
        onConfirm={handleDeleteCase}
        isDeleting={isDeletingCase}
      />

      <DeleteDocumentDialog
        open={!!deleteDocumentTarget}
        onOpenChange={(open) => { if (!open) setDeleteDocumentTarget(null); }}
        documentName={documents.find((d) => d.id === deleteDocumentTarget)?.name ?? ''}
        onConfirm={handleConfirmDeleteDocument}
        isDeleting={isDeletingDocument}
      />

      <ReprocessDocumentDialog
        open={!!reprocessTarget}
        onOpenChange={(open) => { if (!open) setReprocessTarget(null); }}
        documentName={documents.find((d) => d.id === reprocessTarget)?.name ?? ''}
        onConfirm={handleConfirmReprocessDocument}
        isReprocessing={!!reprocessTarget && processingDocumentIds.has(reprocessTarget)}
      />

      {/* Entity type + reason selection for manual redactions */}
      <EntityTypePickerPopover
        isOpen={!!visiblePendingManualRedaction}
        position={visiblePendingManualRedaction?.position ?? { x: window.innerWidth / 2 - 170, y: window.innerHeight / 2 - 200 }}
        extractedText={visiblePendingManualRedaction?.text}
        page={visiblePendingManualRedaction?.kind === 'pdf' ? visiblePendingManualRedaction.page : undefined}
        reasons={reasons}
        onConfirm={handleConfirmEntitySelection}
        onCancel={handleCancelEntitySelection}
      />
    </div>
  );
}
